import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  SystemStatusService,
  SystemOverview,
  ServicesResponse,
  JobsResponse,
  IntegrationsResponse,
  DatabaseResponse,
  UsersResponse,
  ErrorsResponse,
  TimeSeriesResponse,
  EndpointsResponse,
  ApiErrorsResponse,
  TraceLogsResponse,
  EndpointTimeSeriesResponse,
  EndpointErrorsResponse,
  TimeRange,
  createRelativeTimeRange,
  createAbsoluteTimeRange,
  TraceEntry,
  TracesResponse,
  TraceFilters,
  PaginatedTraceLogEntry,
  PaginatedTraceLogsResponse,
  SpanEntry,
  TraceSpansResponse,
  AnalyzeGroup,
  AnalyzeResponse,
  AnalyzeFilters,
  AnalyzeTimeSeriesPoint,
  SearchTracesResponse,
  UserTracesResponse,
  UserInfo,
  UserDeviceInfo,
  UserTraceEntry,
} from "@/services/SystemStatusService";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ServerIcon,
  CircleStackIcon,
  UsersIcon,
  BoltIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CpuChipIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

type TabId = "overview" | "services" | "jobs" | "integrations" | "database" | "users" | "errors";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ElementType;
}

type SortDirection = "asc" | "desc";
type TableSortKey = "name" | "rowCount" | "size" | "lastRefresh";
type EndpointSortKey = "endpoint" | "method" | "requests" | "serverErrors" | "clientErrors" | "errorRate" | "avgLatencyMs" | "p95LatencyMs" | "p99LatencyMs" | "maxLatencyMs";

interface SortConfig {
  key: TableSortKey;
  direction: SortDirection;
}

interface EndpointSortConfig {
  key: EndpointSortKey;
  direction: SortDirection;
}

const TABS: Tab[] = [
  { id: "overview", label: "Overview", icon: ServerIcon },
  { id: "services", label: "Services", icon: CpuChipIcon },
  { id: "jobs", label: "Jobs", icon: ClockIcon },
  { id: "integrations", label: "Integrations", icon: BoltIcon },
  { id: "database", label: "Database", icon: CircleStackIcon },
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "errors", label: "Errors", icon: ExclamationCircleIcon },
];

const TIME_RANGES = [
  { value: 1/60, label: "1m" },
  { value: 5/60, label: "5m" },
  { value: 15/60, label: "15m" },
  { value: 1, label: "1h" },
  { value: 6, label: "6h" },
  { value: 24, label: "24h" },
  { value: 48, label: "2d" },
  { value: 120, label: "5d" },
  { value: 168, label: "7d" },
  { value: 336, label: "14d" },
  { value: 720, label: "30d" },
];

// Helper to determine the appropriate interval based on time range
const getInterval = (hours: number): string => {
  if (hours < 1) return "minute";
  if (hours > 48) return "day";
  return "hour";
};

const SystemStatusPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitialized = useRef(false);

  // Initialize state from URL params
  const initialTab = (searchParams.get('tab') as TabId) || 'overview';
  const initialView = (searchParams.get('view') as 'services' | 'traces' | 'analyze') || 'services';

  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [timeRange, setTimeRange] = useState<TimeRange>(createRelativeTimeRange(24));
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Custom date range state
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  // Derived hours for backward compatibility
  const hours = timeRange.hours;

  // Data states
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [services, setServices] = useState<ServicesResponse | null>(null);
  const [jobs, setJobs] = useState<JobsResponse | null>(null);
  const [integrations, setIntegrations] = useState<IntegrationsResponse | null>(null);
  const [database, setDatabase] = useState<DatabaseResponse | null>(null);
  const [users, setUsers] = useState<UsersResponse | null>(null);
  const [errors, setErrors] = useState<ErrorsResponse | null>(null);
  const [timeSeries, setTimeSeries] = useState<TimeSeriesResponse | null>(null);
  const [showCharts, setShowCharts] = useState(true);

  // Service charts state
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
  const [serviceTimeSeries, setServiceTimeSeries] = useState<Record<string, TimeSeriesResponse | null>>({});
  const [loadingServiceCharts, setLoadingServiceCharts] = useState<Set<string>>(new Set());

  // Sorting states for database tables
  const [mvSort, setMvSort] = useState<SortConfig>({ key: "name", direction: "asc" });
  const [tableSort, setTableSort] = useState<SortConfig>({ key: "size", direction: "desc" });

  // Endpoint metrics state for services
  const [serviceEndpoints, setServiceEndpoints] = useState<Record<string, EndpointsResponse | null>>({});
  const [loadingEndpoints, setLoadingEndpoints] = useState<Set<string>>(new Set());
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set());
  const [endpointSort, setEndpointSort] = useState<Record<string, EndpointSortConfig>>({});
  const [expandedEndpointGraphs, setExpandedEndpointGraphs] = useState<Set<string>>(new Set()); // "serviceName:endpoint:method"
  const [endpointTimeSeries, setEndpointTimeSeries] = useState<Record<string, EndpointTimeSeriesResponse | null>>({});
  const [loadingEndpointTimeSeries, setLoadingEndpointTimeSeries] = useState<Set<string>>(new Set());
  const [endpointApiErrors, setEndpointApiErrors] = useState<Record<string, EndpointErrorsResponse | null>>({});
  const [endpointApiWarnings, setEndpointApiWarnings] = useState<Record<string, EndpointErrorsResponse | null>>({});
  const [endpointLogTab, setEndpointLogTab] = useState<Record<string, 'errors' | 'warnings'>>({}); // Active tab per endpoint
  const [loadingEndpointErrors, setLoadingEndpointErrors] = useState<Set<string>>(new Set());
  const [loadingEndpointWarnings, setLoadingEndpointWarnings] = useState<Set<string>>(new Set());

  // Chart legend visibility state - tracks hidden series per chart
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, Set<string>>>({});

  // API errors with trace logs state
  const [apiErrors, setApiErrors] = useState<ApiErrorsResponse | null>(null);
  const [loadingApiErrors, setLoadingApiErrors] = useState(false);
  const [expandedTraces, setExpandedTraces] = useState<Set<string>>(new Set());
  const [traceLogs, setTraceLogs] = useState<Record<string, TraceLogsResponse | null>>({});
  const [loadingTraceLogs, setLoadingTraceLogs] = useState<Set<string>>(new Set());

  // Trace explorer state (APM)
  const [traceView, setTraceView] = useState<'services' | 'traces' | 'analyze'>(initialView);
  const [traces, setTraces] = useState<TraceEntry[]>([]);
  const [tracesLoading, setTracesLoading] = useState(false);
  const [tracesOffset, setTracesOffset] = useState(0);
  const [tracesHasMore, setTracesHasMore] = useState(false);
  const [traceFilters, setTraceFilters] = useState<TraceFilters>({
    service: searchParams.get('service') || null,
    endpoint: null,
    method: null,
    minStatus: null,
    maxStatus: null,
    minLatencyMs: null,
    maxLatencyMs: null,
    traceId: searchParams.get('traceId') || null,
    textSearch: searchParams.get('search') || null,
    userId: searchParams.get('user') || null,
  });
  const [expandedTraceDetails, setExpandedTraceDetails] = useState<Set<string>>(new Set());
  const [traceDetailLogs, setTraceDetailLogs] = useState<Record<string, PaginatedTraceLogEntry[]>>({});
  const [traceLogHasMore, setTraceLogHasMore] = useState<Record<string, boolean>>({});
  const [loadingTraceLogIds, setLoadingTraceLogIds] = useState<Set<string>>(new Set());
  const [traceSortBy, setTraceSortBy] = useState<string>('timestamp');
  const [traceSortOrder, setTraceSortOrder] = useState<string>('desc');

  // Trace detail tabs state
  type TraceDetailTab = 'info' | 'waterfall' | 'logs' | 'http';
  const [traceDetailTab, setTraceDetailTab] = useState<Record<string, TraceDetailTab>>({});
  const [traceSpans, setTraceSpans] = useState<Record<string, SpanEntry[]>>({});
  const [loadingTraceSpans, setLoadingTraceSpans] = useState<Set<string>>(new Set());

  // Trace Analyze state
  const [analyzeGroupBy, setAnalyzeGroupBy] = useState<string>('service');
  const [analyzeMetric, setAnalyzeMetric] = useState<string>('requests');
  const [analyzeData, setAnalyzeData] = useState<AnalyzeResponse | null>(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeFilters, setAnalyzeFilters] = useState<AnalyzeFilters>({
    service: null, endpoint: null, method: null,
    minStatus: null, maxStatus: null, minLatencyMs: null, maxLatencyMs: null,
  });
  const [analyzeShowTimeSeries, setAnalyzeShowTimeSeries] = useState(false);

  // User-scoped trace search state
  const [userTraceInfo, setUserTraceInfo] = useState<{ user: UserInfo; devices: UserDeviceInfo[] } | null>(null);
  const [userDeviceFilter, setUserDeviceFilter] = useState<number | null>(null);

  // Keyboard navigation for trace explorer
  const [focusedTraceIndex, setFocusedTraceIndex] = useState<number>(-1);

  useEffect(() => {
    if (traceView !== 'traces' || traces.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle when not typing in an input
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'SELECT') return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedTraceIndex(prev => Math.min(prev + 1, traces.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedTraceIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedTraceIndex >= 0 && focusedTraceIndex < traces.length) {
            toggleTraceDetail(traces[focusedTraceIndex].traceId);
          }
          break;
        case 'Escape':
          setFocusedTraceIndex(-1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [traceView, traces, focusedTraceIndex]);

  // Sync URL params when tab/view changes
  useEffect(() => {
    if (!urlInitialized.current) {
      urlInitialized.current = true;
      return;
    }
    const params = new URLSearchParams();
    if (activeTab !== 'overview') params.set('tab', activeTab);
    if (activeTab === 'services' && traceView !== 'services') params.set('view', traceView);
    if (traceFilters.service) params.set('service', traceFilters.service);
    if (traceFilters.traceId) params.set('traceId', traceFilters.traceId);
    if (traceFilters.textSearch) params.set('search', traceFilters.textSearch);
    if (traceFilters.userId) params.set('user', traceFilters.userId);
    setSearchParams(params, { replace: true });
  }, [activeTab, traceView, traceFilters.service, traceFilters.traceId, traceFilters.textSearch, traceFilters.userId]);

  const loadData = useCallback(async () => {
    if (!user?.accesstoken) return;

    setLoading(true);
    try {
      switch (activeTab) {
        case "overview":
          const [overviewData, timeSeriesData] = await Promise.all([
            SystemStatusService.getOverview(user.accesstoken, hours, timeRange),
            SystemStatusService.getTimeSeries(user.accesstoken, hours, getInterval(hours), timeRange),
          ]);
          setOverview(overviewData);
          setTimeSeries(timeSeriesData);
          break;
        case "services":
          const servicesData = await SystemStatusService.getServices(user.accesstoken, hours, timeRange);
          setServices(servicesData);
          break;
        case "jobs":
          const jobsData = await SystemStatusService.getJobs(user.accesstoken, hours, timeRange);
          setJobs(jobsData);
          break;
        case "integrations":
          const integrationsData = await SystemStatusService.getIntegrations(user.accesstoken, hours, timeRange);
          setIntegrations(integrationsData);
          break;
        case "database":
          const databaseData = await SystemStatusService.getDatabase(user.accesstoken);
          setDatabase(databaseData);
          break;
        case "users":
          const usersData = await SystemStatusService.getUsers(user.accesstoken, hours, timeRange);
          setUsers(usersData);
          break;
        case "errors":
          const errorsData = await SystemStatusService.getErrors(user.accesstoken, hours, timeRange);
          setErrors(errorsData);
          break;
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.accesstoken, activeTab, timeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      loadData();
    }, 60000); // 1 minute
    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  // Clear endpoint caches when time range changes
  useEffect(() => {
    setEndpointTimeSeries({});
    setEndpointApiErrors({});
    setEndpointApiWarnings({});
    setServiceTimeSeries({});
    setServiceEndpoints({});
    // Also reset trace explorer data
    setTraces([]);
    setTracesOffset(0);
    setTracesHasMore(false);
  }, [timeRange]);

  const getHealthIcon = (health: string) => {
    switch (health) {
      case "HEALTHY":
        return <CheckCircleIcon className="w-5 h-5 text-green-400" />;
      case "DEGRADED":
      case "WARNING":
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400" />;
      case "CRITICAL":
        return <XCircleIcon className="w-5 h-5 text-red-400" />;
      default:
        return <ExclamationCircleIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getHealthBadge = (health: string) => {
    const colors: Record<string, string> = {
      HEALTHY: "bg-green-500/20 text-green-400 border-green-500/30",
      DEGRADED: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      WARNING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      CRITICAL: "bg-red-500/20 text-red-400 border-red-500/30",
      UNKNOWN: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    };
    return colors[health] || colors.UNKNOWN;
  };

  const formatTimestamp = (timestamp: string | number | null | undefined) => {
    if (timestamp === null || timestamp === undefined) return "N/A";

    let date: Date;
    if (typeof timestamp === 'number') {
      // Handle Unix timestamps (seconds or milliseconds)
      date = new Date(timestamp > 1e12 ? timestamp : timestamp * 1000);
    } else if (typeof timestamp === 'string') {
      // Handle BigQuery format "2024-02-04 12:00:00.123456 UTC" by converting to ISO 8601
      let normalizedTimestamp = timestamp;
      if (timestamp.includes(" UTC")) {
        normalizedTimestamp = timestamp.replace(" UTC", "Z").replace(" ", "T");
      } else if (timestamp.includes(" ") && !timestamp.includes("T")) {
        // Handle "2024-02-04 12:00:00" format without timezone
        normalizedTimestamp = timestamp.replace(" ", "T") + "Z";
      }
      date = new Date(normalizedTimestamp);
    } else {
      return "N/A";
    }
    return date.toLocaleString();
  };

  const formatRelativeTime = (timestamp: string | number | null | undefined) => {
    if (timestamp === null || timestamp === undefined) return null;

    let date: Date;
    if (typeof timestamp === 'number') {
      // Handle Unix timestamps (seconds or milliseconds)
      date = new Date(timestamp > 1e12 ? timestamp : timestamp * 1000);
    } else if (typeof timestamp === 'string') {
      // Handle BigQuery format "2024-02-04 12:00:00.123456 UTC" by converting to ISO 8601
      let normalizedTimestamp = timestamp;
      if (timestamp.includes(" UTC")) {
        normalizedTimestamp = timestamp.replace(" UTC", "Z").replace(" ", "T");
      } else if (timestamp.includes(" ") && !timestamp.includes("T")) {
        normalizedTimestamp = timestamp.replace(" ", "T") + "Z";
      }
      date = new Date(normalizedTimestamp);
    } else {
      return null;
    }
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Parse size string to bytes for sorting (e.g., "88 GB" -> bytes)
  const parseSizeToBytes = (size: string): number => {
    if (!size) return 0;
    const match = size.match(/^([\d.]+)\s*(bytes?|KB|MB|GB|TB|kB)?$/i);
    if (!match) return 0;
    const value = parseFloat(match[1]);
    const unit = (match[2] || "bytes").toUpperCase();
    const multipliers: Record<string, number> = {
      BYTES: 1, BYTE: 1,
      KB: 1024,
      MB: 1024 * 1024,
      GB: 1024 * 1024 * 1024,
      TB: 1024 * 1024 * 1024 * 1024,
    };
    return value * (multipliers[unit] || 1);
  };

  const handleSort = (
    currentSort: SortConfig,
    setSort: React.Dispatch<React.SetStateAction<SortConfig>>,
    key: TableSortKey
  ) => {
    setSort({
      key,
      direction: currentSort.key === key && currentSort.direction === "asc" ? "desc" : "asc",
    });
  };

  const SortIcon: React.FC<{ sortKey: TableSortKey; currentSort: SortConfig }> = ({ sortKey, currentSort }) => {
    if (currentSort.key !== sortKey) {
      return <span className="text-gray-600 ml-1">↕</span>;
    }
    return <span className="text-blue-400 ml-1">{currentSort.direction === "asc" ? "↑" : "↓"}</span>;
  };

  // Format chart timestamp - handles both Unix seconds and milliseconds, and string timestamps
  const formatChartTime = (timestamp: number | string) => {
    let date: Date;
    if (typeof timestamp === 'string') {
      // Handle string timestamps (BigQuery format or ISO)
      let normalized = timestamp;
      if (timestamp.includes(" UTC")) {
        normalized = timestamp.replace(" UTC", "Z").replace(" ", "T");
      } else if (timestamp.includes(" ") && !timestamp.includes("T")) {
        normalized = timestamp.replace(" ", "T") + "Z";
      }
      date = new Date(normalized);
    } else if (timestamp > 1e12) {
      // Timestamp is in milliseconds
      date = new Date(timestamp);
    } else {
      // Timestamp is in seconds
      date = new Date(timestamp * 1000);
    }
    if (hours <= 24) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  // Toggle series visibility in charts
  const toggleSeriesVisibility = (chartId: string, seriesName: string) => {
    setHiddenSeries(prev => {
      const chartHidden = prev[chartId] || new Set<string>();
      const newHidden = new Set(chartHidden);
      if (newHidden.has(seriesName)) {
        newHidden.delete(seriesName);
      } else {
        newHidden.add(seriesName);
      }
      return { ...prev, [chartId]: newHidden };
    });
  };

  // Check if a series is hidden
  const isSeriesHidden = (chartId: string, seriesName: string): boolean => {
    return hiddenSeries[chartId]?.has(seriesName) || false;
  };

  // Custom legend with click-to-toggle functionality
  const renderToggleableLegend = (chartId: string, items: Array<{ name: string; color: string }>) => {
    return (
      <div className="flex flex-wrap gap-2 justify-center mt-1">
        {items.map((item) => {
          const hidden = isSeriesHidden(chartId, item.name);
          return (
            <button
              key={item.name}
              onClick={() => toggleSeriesVisibility(chartId, item.name)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs transition-all ${
                hidden ? 'opacity-40 line-through' : 'opacity-100'
              } hover:bg-gray-700/50`}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-300">{item.name}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // Custom tooltip for charts - handles both Unix seconds and milliseconds, and string timestamps
  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: number | string }) => {
    if (active && payload && payload.length && label) {
      let date: Date;
      if (typeof label === 'string') {
        // Handle string timestamps (BigQuery format or ISO)
        let normalized = label;
        if (label.includes(" UTC")) {
          normalized = label.replace(" UTC", "Z").replace(" ", "T");
        } else if (label.includes(" ") && !label.includes("T")) {
          normalized = label.replace(" ", "T") + "Z";
        }
        date = new Date(normalized);
      } else if (label > 1e12) {
        // Timestamp is in milliseconds
        date = new Date(label);
      } else {
        // Timestamp is in seconds
        date = new Date(label * 1000);
      }
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-400 text-xs mb-2">{date.toLocaleString()}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === "number" ? entry.value.toLocaleString() : entry.value}
              {entry.name.includes("Rate") ? "%" : entry.name.includes("Latency") ? "ms" : ""}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderOverviewTab = () => {
    if (!overview) return <div className="text-gray-400">No data available</div>;

    // Prepare chart data
    const chartData = timeSeries?.data?.map((d) => ({
      ...d,
      time: d.timestamp,
    })) || [];

    return (
      <div className="space-y-6">
        {/* Health Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getHealthIcon(overview.health)}
            <span className={`text-lg font-semibold ${
              overview.health === "HEALTHY" ? "text-green-400" :
              overview.health === "DEGRADED" ? "text-yellow-400" : "text-red-400"
            }`}>
              System {overview.health}
            </span>
          </div>
          <button
            onClick={() => setShowCharts(!showCharts)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
              showCharts ? "bg-blue-500/20 text-blue-400" : "bg-gray-700 text-gray-400"
            }`}
          >
            <ChartBarIcon className="w-4 h-4" />
            Charts
          </button>
        </div>

        {/* Application Metrics (from PostgreSQL) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Sessions Uploaded</div>
            <div className="text-2xl font-bold text-white mt-1">
              {overview.metrics.sessionsUploaded?.toLocaleString() ?? "N/A"}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">New Users</div>
            <div className="text-2xl font-bold text-blue-400 mt-1">
              {overview.metrics.newUsers?.toLocaleString() ?? "N/A"}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Active Users</div>
            <div className="text-2xl font-bold text-green-400 mt-1">
              {overview.metrics.activeUsers?.toLocaleString() ?? "N/A"}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Error Rate</div>
            <div className={`text-2xl font-bold mt-1 ${
              (overview.metrics.errorRate ?? 0) > 1 ? "text-red-400" :
              (overview.metrics.errorRate ?? 0) > 0.5 ? "text-yellow-400" : "text-green-400"
            }`}>
              {overview.metrics.errorRate?.toFixed(2) ?? "0"}%
            </div>
          </div>
        </div>

        {/* API Traffic Metrics (from BigQuery) */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">API Traffic ({hours}h)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="text-gray-500 text-xs">Total Requests</div>
              <div className="text-xl font-bold text-white">
                {overview.metrics.totalRequests?.toLocaleString() ?? "N/A"}
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Server Errors (5xx)</div>
              <div className="text-xl font-bold text-red-400">
                {overview.metrics.totalErrors?.toLocaleString() ?? "0"}
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Error Rate</div>
              <div className={`text-xl font-bold ${
                (overview.metrics.errorRate ?? 0) > 1 ? "text-red-400" : "text-green-400"
              }`}>
                {overview.metrics.errorRate?.toFixed(2) ?? "0"}%
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Avg Latency</div>
              <div className="text-xl font-bold text-white">
                {overview.metrics.avgLatencyMs?.toFixed(0) ?? "N/A"}
                <span className="text-sm text-gray-500">ms</span>
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">P95 Latency</div>
              <div className="text-xl font-bold text-white">
                {overview.metrics.p95LatencyMs?.toFixed(0) ?? "N/A"}
                <span className="text-sm text-gray-500">ms</span>
              </div>
            </div>
          </div>
        </div>

        {/* Time Series Charts */}
        {showCharts && chartData.length > 0 && (
          <div className="space-y-4">
            {/* Requests Chart */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Request Volume</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="time"
                    tickFormatter={formatChartTime}
                    stroke="#6B7280"
                    fontSize={11}
                  />
                  <YAxis stroke="#6B7280" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="requests"
                    name="Requests"
                    stroke="#3B82F6"
                    fillOpacity={1}
                    fill="url(#colorRequests)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Errors Chart */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Errors <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorErrors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorClientErrors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="time"
                    tickFormatter={formatChartTime}
                    stroke="#6B7280"
                    fontSize={11}
                  />
                  <YAxis stroke="#6B7280" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  {!isSeriesHidden("overview:errors", "5xx Errors") && (
                    <Area
                      type="monotone"
                      dataKey="errors"
                      name="5xx Errors"
                      stroke="#EF4444"
                      fillOpacity={1}
                      fill="url(#colorErrors)"
                    />
                  )}
                  {!isSeriesHidden("overview:errors", "4xx Errors") && (
                    <Area
                      type="monotone"
                      dataKey="clientErrors"
                      name="4xx Errors"
                      stroke="#F59E0B"
                      fillOpacity={1}
                      fill="url(#colorClientErrors)"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
              {renderToggleableLegend("overview:errors", [
                { name: "5xx Errors", color: "#EF4444" },
                { name: "4xx Errors", color: "#F59E0B" },
              ])}
            </div>

            {/* Latency Chart */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Response Latency <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="time"
                    tickFormatter={formatChartTime}
                    stroke="#6B7280"
                    fontSize={11}
                  />
                  <YAxis stroke="#6B7280" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  {!isSeriesHidden("overview:latency", "Avg Latency") && (
                    <Line
                      type="monotone"
                      dataKey="avgLatencyMs"
                      name="Avg Latency"
                      stroke="#10B981"
                      dot={false}
                      strokeWidth={2}
                    />
                  )}
                  {!isSeriesHidden("overview:latency", "P50 Latency") && (
                    <Line
                      type="monotone"
                      dataKey="p50LatencyMs"
                      name="P50 Latency"
                      stroke="#3B82F6"
                      dot={false}
                      strokeWidth={1}
                    />
                  )}
                  {!isSeriesHidden("overview:latency", "P95 Latency") && (
                    <Line
                      type="monotone"
                      dataKey="p95LatencyMs"
                      name="P95 Latency"
                      stroke="#F59E0B"
                      dot={false}
                      strokeWidth={2}
                    />
                  )}
                  {!isSeriesHidden("overview:latency", "P99 Latency") && (
                    <Line
                      type="monotone"
                      dataKey="p99LatencyMs"
                      name="P99 Latency"
                      stroke="#EF4444"
                      dot={false}
                      strokeWidth={1}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              {renderToggleableLegend("overview:latency", [
                { name: "Avg Latency", color: "#10B981" },
                { name: "P50 Latency", color: "#3B82F6" },
                { name: "P95 Latency", color: "#F59E0B" },
                { name: "P99 Latency", color: "#EF4444" },
              ])}
            </div>

            {/* Unique IPs Chart */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Unique Visitors (IPs)</h3>
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorIps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="time"
                    tickFormatter={formatChartTime}
                    stroke="#6B7280"
                    fontSize={11}
                  />
                  <YAxis stroke="#6B7280" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="uniqueIps"
                    name="Unique IPs"
                    stroke="#8B5CF6"
                    fillOpacity={1}
                    fill="url(#colorIps)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {showCharts && chartData.length === 0 && (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <ChartBarIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No time series data available</p>
            <p className="text-gray-500 text-sm mt-1">BigQuery metrics will appear here when available</p>
          </div>
        )}

        {/* Integration Status */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Integration Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(overview.integrations).map(([name, status]) => (
              <div key={name} className="flex items-center gap-2">
                {getHealthIcon(status)}
                <span className="text-gray-300 capitalize">{name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        {overview.alerts && overview.alerts.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Active Alerts</h3>
            <div className="space-y-2">
              {overview.alerts.map((alert, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    alert.severity === "CRITICAL" ? "bg-red-500/10" :
                    alert.severity === "WARNING" ? "bg-yellow-500/10" : "bg-blue-500/10"
                  }`}
                >
                  {alert.severity === "CRITICAL" ? (
                    <XCircleIcon className="w-5 h-5 text-red-400" />
                  ) : alert.severity === "WARNING" ? (
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400" />
                  ) : (
                    <ExclamationCircleIcon className="w-5 h-5 text-blue-400" />
                  )}
                  <div>
                    <span className="text-gray-400 text-sm">{alert.component}: </span>
                    <span className="text-gray-200">{alert.message}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Toggle service expansion and load charts
  const toggleServiceExpanded = async (serviceName: string) => {
    const newExpanded = new Set(expandedServices);

    if (newExpanded.has(serviceName)) {
      newExpanded.delete(serviceName);
    } else {
      newExpanded.add(serviceName);

      // Load time series data if not already loaded
      if (!serviceTimeSeries[serviceName] && user?.accesstoken) {
        setLoadingServiceCharts(prev => new Set(prev).add(serviceName));
        try {
          const data = await SystemStatusService.getServiceTimeSeries(
            user.accesstoken,
            serviceName,
            hours,
            getInterval(hours),
            timeRange
          );
          setServiceTimeSeries(prev => ({ ...prev, [serviceName]: data }));
        } catch (error) {
          console.error("Error loading service charts:", error);
        } finally {
          setLoadingServiceCharts(prev => {
            const next = new Set(prev);
            next.delete(serviceName);
            return next;
          });
        }
      }
    }

    setExpandedServices(newExpanded);
  };

  // Render mini charts for a service
  const renderServiceCharts = (serviceName: string) => {
    const data = serviceTimeSeries[serviceName];
    const isLoading = loadingServiceCharts.has(serviceName);

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-400 text-sm">Loading charts...</span>
        </div>
      );
    }

    if (!data?.data?.length) {
      return (
        <div className="text-center py-6 text-gray-500 text-sm">
          No time series data available for this service
        </div>
      );
    }

    const chartData = data.data.map(d => ({ ...d, time: d.timestamp }));

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Requests Chart */}
        <div className="bg-gray-900/50 rounded-lg p-3">
          <h4 className="text-xs font-medium text-gray-400 mb-2">Requests</h4>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id={`colorReq${serviceName}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" tickFormatter={formatChartTime} stroke="#6B7280" fontSize={9} />
              <YAxis stroke="#6B7280" fontSize={9} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="requests" stroke="#3B82F6" fillOpacity={1} fill={`url(#colorReq${serviceName})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Errors Chart */}
        <div className="bg-gray-900/50 rounded-lg p-3">
          <h4 className="text-xs font-medium text-gray-400 mb-2">Errors</h4>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id={`colorErr${serviceName}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" tickFormatter={formatChartTime} stroke="#6B7280" fontSize={9} />
              <YAxis stroke="#6B7280" fontSize={9} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="errors" name="5xx" stroke="#EF4444" fillOpacity={1} fill={`url(#colorErr${serviceName})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Latency Chart */}
        <div className="bg-gray-900/50 rounded-lg p-3 lg:col-span-2">
          <h4 className="text-xs font-medium text-gray-400 mb-2">Response Latency (ms) <span className="text-gray-500 font-normal">(click legend to toggle)</span></h4>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" tickFormatter={formatChartTime} stroke="#6B7280" fontSize={9} />
              <YAxis stroke="#6B7280" fontSize={9} />
              <Tooltip content={<CustomTooltip />} />
              {!isSeriesHidden(`service:${serviceName}:latency`, "Avg") && (
                <Line type="monotone" dataKey="avgLatencyMs" name="Avg" stroke="#10B981" dot={false} strokeWidth={1.5} />
              )}
              {!isSeriesHidden(`service:${serviceName}:latency`, "P95") && (
                <Line type="monotone" dataKey="p95LatencyMs" name="P95" stroke="#F59E0B" dot={false} strokeWidth={1.5} />
              )}
              {!isSeriesHidden(`service:${serviceName}:latency`, "P99") && (
                <Line type="monotone" dataKey="p99LatencyMs" name="P99" stroke="#EF4444" dot={false} strokeWidth={1} />
              )}
            </LineChart>
          </ResponsiveContainer>
          {renderToggleableLegend(`service:${serviceName}:latency`, [
            { name: "Avg", color: "#10B981" },
            { name: "P95", color: "#F59E0B" },
            { name: "P99", color: "#EF4444" },
          ])}
        </div>
      </div>
    );
  };

  // Toggle endpoint metrics for a service
  const toggleEndpoints = async (serviceName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger service expand

    const newExpanded = new Set(expandedEndpoints);

    if (newExpanded.has(serviceName)) {
      newExpanded.delete(serviceName);
    } else {
      newExpanded.add(serviceName);

      // Load endpoint data if not already loaded
      if (!serviceEndpoints[serviceName] && user?.accesstoken) {
        setLoadingEndpoints(prev => new Set(prev).add(serviceName));
        try {
          const data = await SystemStatusService.getServiceEndpoints(
            user.accesstoken,
            serviceName,
            hours,
            timeRange
          );
          setServiceEndpoints(prev => ({ ...prev, [serviceName]: data }));
        } catch (error) {
          console.error("Error loading endpoints:", error);
        } finally {
          setLoadingEndpoints(prev => {
            const next = new Set(prev);
            next.delete(serviceName);
            return next;
          });
        }
      }
    }

    setExpandedEndpoints(newExpanded);
  };

  // Render endpoint metrics table for a service
  // Handle endpoint sorting
  const handleEndpointSort = (serviceName: string, key: EndpointSortKey) => {
    const currentSort = endpointSort[serviceName] || { key: "requests", direction: "desc" as SortDirection };
    const newDirection = currentSort.key === key && currentSort.direction === "desc" ? "asc" : "desc";
    setEndpointSort(prev => ({
      ...prev,
      [serviceName]: { key, direction: newDirection }
    }));
  };

  // Endpoint sort icon component
  const EndpointSortIcon: React.FC<{ sortKey: EndpointSortKey; serviceName: string }> = ({ sortKey, serviceName }) => {
    const currentSort = endpointSort[serviceName] || { key: "requests", direction: "desc" };
    if (currentSort.key !== sortKey) return <span className="text-gray-600 ml-1">↕</span>;
    return <span className="text-blue-400 ml-1">{currentSort.direction === "asc" ? "↑" : "↓"}</span>;
  };

  // Toggle endpoint graph and load data
  const toggleEndpointGraph = async (serviceName: string, endpoint: string, method: string) => {
    const key = `${serviceName}:${endpoint}:${method}`;
    const newExpanded = new Set(expandedEndpointGraphs);

    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);

      // Load time series data if not already loaded
      if (!endpointTimeSeries[key] && user?.accesstoken) {
        setLoadingEndpointTimeSeries(prev => new Set(prev).add(key));
        try {
          const data = await SystemStatusService.getEndpointTimeSeries(
            user.accesstoken,
            serviceName,
            endpoint,
            method,
            hours,
            getInterval(hours),
            timeRange
          );
          setEndpointTimeSeries(prev => ({ ...prev, [key]: data }));
        } catch (error) {
          console.error("Error loading endpoint time series:", error);
        } finally {
          setLoadingEndpointTimeSeries(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }
      }
    }

    setExpandedEndpointGraphs(newExpanded);
  };

  // Load endpoint errors (5xx) on demand
  const loadEndpointErrors = async (serviceName: string, endpoint: string, method: string) => {
    const key = `${serviceName}:${endpoint}:${method}`;
    if (loadingEndpointErrors.has(key) || !user?.accesstoken) return;

    setLoadingEndpointErrors(prev => new Set(prev).add(key));
    try {
      const data = await SystemStatusService.getEndpointErrors(
        user.accesstoken,
        serviceName,
        endpoint,
        method,
        hours,
        20,
        500,  // minStatus = 500 for server errors only
        undefined,
        timeRange
      );
      setEndpointApiErrors(prev => ({ ...prev, [key]: data }));
    } catch (error) {
      console.error("Error loading endpoint errors:", error);
    } finally {
      setLoadingEndpointErrors(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Load endpoint warnings (4xx) on demand
  const loadEndpointWarnings = async (serviceName: string, endpoint: string, method: string) => {
    const key = `${serviceName}:${endpoint}:${method}`;
    if (loadingEndpointWarnings.has(key) || !user?.accesstoken) return;

    setLoadingEndpointWarnings(prev => new Set(prev).add(key));
    try {
      const data = await SystemStatusService.getEndpointErrors(
        user.accesstoken,
        serviceName,
        endpoint,
        method,
        hours,
        20,
        400,  // minStatus = 400
        500,  // maxStatus = 500 (exclusive) for client errors only
        timeRange
      );
      setEndpointApiWarnings(prev => ({ ...prev, [key]: data }));
    } catch (error) {
      console.error("Error loading endpoint warnings:", error);
    } finally {
      setLoadingEndpointWarnings(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Render endpoint graph and errors panel
  const renderEndpointDetails = (serviceName: string, endpoint: string, method: string) => {
    const key = `${serviceName}:${endpoint}:${method}`;
    const tsData = endpointTimeSeries[key];
    const errorsData = endpointApiErrors[key];
    const warningsData = endpointApiWarnings[key];
    const isLoadingTs = loadingEndpointTimeSeries.has(key);
    const isLoadingErrors = loadingEndpointErrors.has(key);
    const isLoadingWarnings = loadingEndpointWarnings.has(key);

    const trafficChartId = `${key}:traffic`;
    const latencyChartId = `${key}:latency`;

    const trafficSeries = [
      { name: "Requests", color: "#3B82F6" },
      { name: "5xx Errors", color: "#EF4444" },
      { name: "4xx Warnings", color: "#F59E0B" },
    ];

    const latencySeries = [
      { name: "Avg", color: "#10B981" },
      { name: "P95", color: "#F59E0B" },
      { name: "P99", color: "#EF4444" },
      { name: "Max", color: "#8B5CF6" },
    ];

    return (
      <div className="bg-gray-900/50 rounded-lg p-3 mt-2 space-y-4">
        {/* Time Series Charts */}
        <div>
          <h5 className="text-xs font-medium text-gray-400 mb-2">Traffic & Latency Over Time <span className="text-gray-500 font-normal">(click legend to toggle)</span></h5>
          {isLoadingTs ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-4 h-4 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
              <span className="ml-2 text-gray-400 text-xs">Loading charts...</span>
            </div>
          ) : tsData?.data?.length ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Requests & Errors Chart */}
              <div className="bg-gray-800/50 rounded p-2">
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={tsData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#6B7280" fontSize={9} />
                    <YAxis stroke="#6B7280" fontSize={9} />
                    <Tooltip content={<CustomTooltip />} />
                    {!isSeriesHidden(trafficChartId, "Requests") && (
                      <Area type="monotone" dataKey="requests" name="Requests" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} />
                    )}
                    {!isSeriesHidden(trafficChartId, "5xx Errors") && (
                      <Area type="monotone" dataKey="errors" name="5xx Errors" stroke="#EF4444" fill="#EF4444" fillOpacity={0.3} />
                    )}
                    {!isSeriesHidden(trafficChartId, "4xx Warnings") && (
                      <Area type="monotone" dataKey="clientErrors" name="4xx Warnings" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.2} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
                {renderToggleableLegend(trafficChartId, trafficSeries)}
              </div>
              {/* Latency Chart */}
              <div className="bg-gray-800/50 rounded p-2">
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={tsData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#6B7280" fontSize={9} />
                    <YAxis stroke="#6B7280" fontSize={9} />
                    <Tooltip content={<CustomTooltip />} />
                    {!isSeriesHidden(latencyChartId, "Avg") && (
                      <Line type="monotone" dataKey="avgLatencyMs" name="Avg" stroke="#10B981" dot={false} strokeWidth={1.5} />
                    )}
                    {!isSeriesHidden(latencyChartId, "P95") && (
                      <Line type="monotone" dataKey="p95LatencyMs" name="P95" stroke="#F59E0B" dot={false} strokeWidth={1.5} />
                    )}
                    {!isSeriesHidden(latencyChartId, "P99") && (
                      <Line type="monotone" dataKey="p99LatencyMs" name="P99" stroke="#EF4444" dot={false} strokeWidth={1.5} />
                    )}
                    {!isSeriesHidden(latencyChartId, "Max") && (
                      <Line type="monotone" dataKey="maxLatencyMs" name="Max" stroke="#8B5CF6" dot={false} strokeWidth={1} strokeDasharray="3 3" />
                    )}
                  </LineChart>
                </ResponsiveContainer>
                {renderToggleableLegend(latencyChartId, latencySeries)}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 text-xs">No time series data available</div>
          )}
        </div>

        {/* Errors and Warnings Section - Tabbed */}
        <div>
          {/* Tab Headers */}
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700 pb-2">
            <button
              onClick={() => setEndpointLogTab(prev => ({ ...prev, [key]: 'errors' }))}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${
                (endpointLogTab[key] || 'errors') === 'errors'
                  ? 'bg-red-900/40 text-red-300 border-b-2 border-red-500'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>Server Errors (5xx)</span>
              {errorsData?.errors?.length ? (
                <span className="px-1.5 py-0.5 bg-red-500/30 rounded text-red-300">{errorsData.errors.length}</span>
              ) : null}
            </button>
            <button
              onClick={() => setEndpointLogTab(prev => ({ ...prev, [key]: 'warnings' }))}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${
                endpointLogTab[key] === 'warnings'
                  ? 'bg-yellow-900/40 text-yellow-300 border-b-2 border-yellow-500'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>Client Warnings (4xx)</span>
              {warningsData?.errors?.length ? (
                <span className="px-1.5 py-0.5 bg-yellow-500/30 rounded text-yellow-300">{warningsData.errors.length}</span>
              ) : null}
            </button>
          </div>

          {/* Tab Content */}
          {(endpointLogTab[key] || 'errors') === 'errors' ? (
            /* Server Errors (5xx) */
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">
                  {errorsData?.errors?.length ? `${errorsData.errors.length} errors found` : 'No data loaded'}
                </span>
                <button
                  onClick={() => loadEndpointErrors(serviceName, endpoint, method)}
                  disabled={isLoadingErrors}
                  className="flex items-center gap-1 px-2 py-1 bg-red-900/30 hover:bg-red-900/50 disabled:bg-gray-800 rounded text-xs text-red-300 transition-colors"
                >
                  {isLoadingErrors ? (
                    <>
                      <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-3 h-3" />
                      Load Errors
                    </>
                  )}
                </button>
              </div>

              {errorsData?.errors?.length ? (
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {errorsData.errors.map((err, idx) => {
                    const isExpanded = err.traceId && expandedTraces.has(err.traceId);
                    return (
                      <div key={idx} className="border border-red-500/20 rounded overflow-hidden">
                        <div
                          onClick={() => err.traceId && toggleTraceLogs(err.traceId)}
                          className={`flex items-center gap-2 p-2 text-xs bg-red-500/10 ${
                            err.traceId ? 'cursor-pointer hover:bg-red-500/20' : ''
                          }`}
                        >
                          {err.traceId && (
                            isExpanded ? (
                              <ChevronDownIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            ) : (
                              <ChevronRightIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            )
                          )}
                          <span className="px-1 rounded font-medium bg-red-500/30 text-red-300">
                            {err.status}
                          </span>
                          <span className="text-gray-400">{formatTimestamp(err.timestamp)}</span>
                          <span className="text-gray-300 truncate flex-1">{err.endpoint}</span>
                          {err.latencyMs && <span className="text-gray-500">{err.latencyMs.toFixed(0)}ms</span>}
                          {loadingTraceLogs.has(err.traceId || '') && (
                            <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                          )}
                        </div>
                        {/* Trace Logs */}
                        {isExpanded && err.traceId && renderTraceLogs(err.traceId)}
                      </div>
                    );
                  })}
                </div>
              ) : errorsData ? (
                <div className="text-center py-4 text-green-400 text-xs">No server errors found</div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-xs">Click "Load Errors" to fetch error logs</div>
              )}
            </div>
          ) : (
            /* Client Warnings (4xx) */
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">
                  {warningsData?.errors?.length ? `${warningsData.errors.length} warnings found` : 'No data loaded'}
                </span>
                <button
                  onClick={() => loadEndpointWarnings(serviceName, endpoint, method)}
                  disabled={isLoadingWarnings}
                  className="flex items-center gap-1 px-2 py-1 bg-yellow-900/30 hover:bg-yellow-900/50 disabled:bg-gray-800 rounded text-xs text-yellow-300 transition-colors"
                >
                  {isLoadingWarnings ? (
                    <>
                      <div className="w-3 h-3 border border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-3 h-3" />
                      Load Warnings
                    </>
                  )}
                </button>
              </div>

              {warningsData?.errors?.length ? (
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {warningsData.errors.map((err, idx) => {
                    const isExpanded = err.traceId && expandedTraces.has(err.traceId);
                    return (
                      <div key={idx} className="border border-yellow-500/20 rounded overflow-hidden">
                        <div
                          onClick={() => err.traceId && toggleTraceLogs(err.traceId)}
                          className={`flex items-center gap-2 p-2 text-xs bg-yellow-500/10 ${
                            err.traceId ? 'cursor-pointer hover:bg-yellow-500/20' : ''
                          }`}
                        >
                          {err.traceId && (
                            isExpanded ? (
                              <ChevronDownIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            ) : (
                              <ChevronRightIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            )
                          )}
                          <span className="px-1 rounded font-medium bg-yellow-500/30 text-yellow-300">
                            {err.status}
                          </span>
                          <span className="text-gray-400">{formatTimestamp(err.timestamp)}</span>
                          <span className="text-gray-300 truncate flex-1">{err.endpoint}</span>
                          {err.latencyMs && <span className="text-gray-500">{err.latencyMs.toFixed(0)}ms</span>}
                          {loadingTraceLogs.has(err.traceId || '') && (
                            <div className="w-3 h-3 border border-yellow-400 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                          )}
                        </div>
                        {/* Trace Logs */}
                        {isExpanded && err.traceId && renderTraceLogs(err.traceId)}
                      </div>
                    );
                  })}
                </div>
              ) : warningsData ? (
                <div className="text-center py-4 text-green-400 text-xs">No client warnings found</div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-xs">Click "Load Warnings" to fetch warning logs</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEndpointMetrics = (serviceName: string) => {
    const data = serviceEndpoints[serviceName];
    const isLoading = loadingEndpoints.has(serviceName);
    const currentSort = endpointSort[serviceName] || { key: "requests" as EndpointSortKey, direction: "desc" as SortDirection };

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-4">
          <div className="w-5 h-5 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-400 text-sm">Loading endpoints...</span>
        </div>
      );
    }

    if (!data?.endpoints?.length) {
      return (
        <div className="text-center py-4 text-gray-500 text-sm">
          No endpoint data available
        </div>
      );
    }

    // Sort endpoints
    const sortedEndpoints = [...data.endpoints].sort((a, b) => {
      const direction = currentSort.direction === "asc" ? 1 : -1;
      switch (currentSort.key) {
        case "endpoint":
          return direction * a.endpoint.localeCompare(b.endpoint);
        case "method":
          return direction * a.method.localeCompare(b.method);
        case "requests":
          return direction * (a.requests - b.requests);
        case "serverErrors":
          return direction * (a.serverErrors - b.serverErrors);
        case "clientErrors":
          return direction * (a.clientErrors - b.clientErrors);
        case "errorRate":
          return direction * (a.errorRate - b.errorRate);
        case "avgLatencyMs":
          return direction * ((a.avgLatencyMs ?? 0) - (b.avgLatencyMs ?? 0));
        case "p95LatencyMs":
          return direction * ((a.p95LatencyMs ?? 0) - (b.p95LatencyMs ?? 0));
        case "p99LatencyMs":
          return direction * ((a.p99LatencyMs ?? 0) - (b.p99LatencyMs ?? 0));
        case "maxLatencyMs":
          return direction * ((a.maxLatencyMs ?? 0) - (b.maxLatencyMs ?? 0));
        default:
          return 0;
      }
    });

    return (
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 text-left border-b border-gray-700">
              <th className="pb-2 pr-1 w-6"></th>
              <th
                className="pb-2 pr-4 cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "endpoint")}
              >
                Endpoint <EndpointSortIcon sortKey="endpoint" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-center cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "method")}
              >
                Method <EndpointSortIcon sortKey="method" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "requests")}
              >
                Requests <EndpointSortIcon sortKey="requests" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "serverErrors")}
              >
                5xx <EndpointSortIcon sortKey="serverErrors" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "clientErrors")}
              >
                4xx <EndpointSortIcon sortKey="clientErrors" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "errorRate")}
              >
                Error % <EndpointSortIcon sortKey="errorRate" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "avgLatencyMs")}
              >
                Avg (ms) <EndpointSortIcon sortKey="avgLatencyMs" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "p95LatencyMs")}
              >
                P95 (ms) <EndpointSortIcon sortKey="p95LatencyMs" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 pr-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "p99LatencyMs")}
              >
                P99 (ms) <EndpointSortIcon sortKey="p99LatencyMs" serviceName={serviceName} />
              </th>
              <th
                className="pb-2 text-right cursor-pointer hover:text-gray-200 select-none"
                onClick={() => handleEndpointSort(serviceName, "maxLatencyMs")}
              >
                Max (ms) <EndpointSortIcon sortKey="maxLatencyMs" serviceName={serviceName} />
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-300">
            {sortedEndpoints.map((ep, idx) => {
              const graphKey = `${serviceName}:${ep.endpoint}:${ep.method}`;
              const isExpanded = expandedEndpointGraphs.has(graphKey);

              return (
                <React.Fragment key={idx}>
                  <tr
                    className={`border-b border-gray-800 hover:bg-gray-700/30 cursor-pointer ${isExpanded ? 'bg-gray-700/20' : ''}`}
                    onClick={() => toggleEndpointGraph(serviceName, ep.endpoint, ep.method)}
                  >
                    <td className="py-2 pr-1">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td className="py-2 pr-4 font-mono truncate max-w-xs" title={ep.endpoint}>
                      {ep.endpoint}
                    </td>
                    <td className="py-2 pr-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        ep.method === 'GET' ? 'bg-green-500/20 text-green-400' :
                        ep.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                        ep.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                        ep.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {ep.method}
                      </span>
                    </td>
                    <td className="py-2 pr-2 text-right">{ep.requests.toLocaleString()}</td>
                    <td className={`py-2 pr-2 text-right ${ep.serverErrors > 0 ? 'text-red-400' : ''}`}>
                      {ep.serverErrors}
                    </td>
                    <td className={`py-2 pr-2 text-right ${ep.clientErrors > 0 ? 'text-yellow-400' : ''}`}>
                      {ep.clientErrors}
                    </td>
                    <td className={`py-2 pr-2 text-right ${
                      ep.errorRate > 1 ? 'text-red-400' :
                      ep.errorRate > 0.5 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {ep.errorRate.toFixed(2)}%
                    </td>
                    <td className="py-2 pr-2 text-right">{ep.avgLatencyMs?.toFixed(0) ?? '-'}</td>
                    <td className="py-2 pr-2 text-right">{ep.p95LatencyMs?.toFixed(0) ?? '-'}</td>
                    <td className="py-2 pr-2 text-right">{ep.p99LatencyMs?.toFixed(0) ?? '-'}</td>
                    <td className={`py-2 text-right ${(ep.maxLatencyMs ?? 0) > 10000 ? 'text-yellow-400' : ''}`}>
                      {ep.maxLatencyMs?.toFixed(0) ?? '-'}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={11} className="p-0">
                        {renderEndpointDetails(serviceName, ep.endpoint, ep.method)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Load API errors on demand
  const loadApiErrors = async () => {
    if (!user?.accesstoken || loadingApiErrors) return;

    setLoadingApiErrors(true);
    try {
      const data = await SystemStatusService.getApiErrors(user.accesstoken, hours, 50, undefined, timeRange);
      setApiErrors(data);
    } catch (error) {
      console.error("Error loading API errors:", error);
    } finally {
      setLoadingApiErrors(false);
    }
  };

  // Toggle trace log details
  const toggleTraceLogs = async (traceId: string) => {
    const newExpanded = new Set(expandedTraces);

    if (newExpanded.has(traceId)) {
      newExpanded.delete(traceId);
    } else {
      newExpanded.add(traceId);

      // Load trace logs if not already loaded
      if (!traceLogs[traceId] && user?.accesstoken) {
        setLoadingTraceLogs(prev => new Set(prev).add(traceId));
        try {
          const data = await SystemStatusService.getTraceLogs(user.accesstoken, traceId);
          setTraceLogs(prev => ({ ...prev, [traceId]: data }));
        } catch (error) {
          console.error("Error loading trace logs:", error);
        } finally {
          setLoadingTraceLogs(prev => {
            const next = new Set(prev);
            next.delete(traceId);
            return next;
          });
        }
      }
    }

    setExpandedTraces(newExpanded);
  };

  // Render trace log details
  const renderTraceLogs = (traceId: string) => {
    const data = traceLogs[traceId];
    const isLoading = loadingTraceLogs.has(traceId);

    if (isLoading) {
      return (
        <div className="flex items-center py-3 pl-8">
          <div className="w-4 h-4 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-400 text-xs">Loading logs...</span>
        </div>
      );
    }

    if (!data?.logs?.length) {
      return (
        <div className="py-3 pl-8 text-gray-500 text-xs">
          No log entries found for this trace
        </div>
      );
    }

    return (
      <div className="pl-8 py-2 space-y-1 bg-gray-900/50 rounded-b-lg">
        {data.logs.map((log, idx) => (
          <div key={idx} className={`text-xs font-mono p-2 rounded ${
            log.severity === 'ERROR' ? 'bg-red-500/10 border-l-2 border-red-500' :
            log.severity === 'WARNING' ? 'bg-yellow-500/10 border-l-2 border-yellow-500' :
            'bg-gray-800/50'
          }`}>
            <div className="flex items-start gap-2">
              <span className={`px-1 rounded text-[10px] font-medium ${
                log.severity === 'ERROR' ? 'bg-red-500/30 text-red-300' :
                log.severity === 'WARNING' ? 'bg-yellow-500/30 text-yellow-300' :
                log.severity === 'INFO' ? 'bg-blue-500/30 text-blue-300' :
                'bg-gray-500/30 text-gray-300'
              }`}>
                {log.severity}
              </span>
              <span className="text-gray-500">{log.timestamp?.slice(11, 23)}</span>
              {log.sourceLocation && (
                <span className="text-gray-400">
                  {log.sourceLocation.file}:{log.sourceLocation.line}
                </span>
              )}
            </div>
            {log.message && (
              <pre className="mt-1 text-gray-300 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                {log.message}
              </pre>
            )}
            {log.protoPayload && (
              <div className="mt-1 text-gray-400">
                {log.protoPayload.method} {log.protoPayload.resource} → {log.protoPayload.status}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ==================== Trace Explorer Functions ====================

  const loadTraces = async (append: boolean = false) => {
    if (!user?.accesstoken) return;
    const offset = append ? traces.length : 0;
    setTracesLoading(true);

    try {
      // Route to the correct endpoint based on active filters
      if (traceFilters.textSearch && traceFilters.textSearch.length >= 3) {
        // Full-text log search
        const data = await SystemStatusService.searchTraces(
          user.accesstoken,
          timeRange,
          traceFilters.textSearch,
          traceFilters.service,
          50,
          offset
        );
        if (data) {
          setTraces(prev => append ? [...prev, ...data.traces] : data.traces);
          setTracesHasMore(data.hasMore);
          setTracesOffset(offset + data.traces.length);
          if (!append) setUserTraceInfo(null);
        }
      } else if (traceFilters.userId && traceFilters.userId.trim().length > 0) {
        // User-scoped trace search
        const userInput = traceFilters.userId.trim();
        const isNumericId = /^\d+$/.test(userInput);
        const data = await SystemStatusService.getUserTraces(
          user.accesstoken,
          timeRange,
          isNumericId ? null : userInput,     // email
          isNumericId ? userInput : null,      // userId
          userDeviceFilter,
          50,
          offset
        );
        if (data) {
          setTraces(prev => append ? [...prev, ...data.traces] : data.traces);
          setTracesHasMore(data.hasMore);
          setTracesOffset(offset + data.traces.length);
          if (!append) {
            setUserTraceInfo({ user: data.user, devices: data.devices });
          }
        }
      } else {
        // Standard trace search
        const data = await SystemStatusService.getTraces(
          user.accesstoken,
          timeRange,
          traceFilters,
          50,
          offset,
          traceSortBy,
          traceSortOrder
        );
        if (data) {
          setTraces(prev => append ? [...prev, ...data.traces] : data.traces);
          setTracesHasMore(data.hasMore);
          setTracesOffset(offset + data.traces.length);
          if (!append) setUserTraceInfo(null);
        }
      }
    } catch (error) {
      console.error("Error loading traces:", error);
    } finally {
      setTracesLoading(false);
    }
  };

  const loadTraceDetailLogs = async (traceId: string, append: boolean = false) => {
    if (!user?.accesstoken) return;
    const offset = append ? (traceDetailLogs[traceId]?.length || 0) : 0;
    setLoadingTraceLogIds(prev => new Set(prev).add(traceId));

    try {
      const data = await SystemStatusService.getPaginatedTraceLogs(
        user.accesstoken,
        traceId,
        50,
        offset
      );

      if (data) {
        setTraceDetailLogs(prev => ({
          ...prev,
          [traceId]: append ? [...(prev[traceId] || []), ...data.logs] : data.logs,
        }));
        setTraceLogHasMore(prev => ({ ...prev, [traceId]: data.hasMore }));
      }
    } catch (error) {
      console.error("Error loading trace detail logs:", error);
    } finally {
      setLoadingTraceLogIds(prev => {
        const next = new Set(prev);
        next.delete(traceId);
        return next;
      });
    }
  };

  const loadTraceSpans = async (traceId: string) => {
    if (!user?.accesstoken || loadingTraceSpans.has(traceId)) return;
    setLoadingTraceSpans(prev => new Set(prev).add(traceId));

    try {
      const data = await SystemStatusService.getTraceSpans(user.accesstoken, traceId);
      if (data) {
        setTraceSpans(prev => ({ ...prev, [traceId]: data.spans }));
      }
    } catch (error) {
      console.error("Error loading trace spans:", error);
    } finally {
      setLoadingTraceSpans(prev => {
        const next = new Set(prev);
        next.delete(traceId);
        return next;
      });
    }
  };

  const toggleTraceDetail = (traceId: string) => {
    const newExpanded = new Set(expandedTraceDetails);
    if (newExpanded.has(traceId)) {
      newExpanded.delete(traceId);
    } else {
      newExpanded.add(traceId);
      // Load logs if not already loaded
      if (!traceDetailLogs[traceId]) {
        loadTraceDetailLogs(traceId);
      }
    }
    setExpandedTraceDetails(newExpanded);
  };

  const switchTraceDetailTab = (traceId: string, tab: TraceDetailTab) => {
    setTraceDetailTab(prev => ({ ...prev, [traceId]: tab }));
    // Lazy-load data for the tab
    if (tab === 'waterfall' && !traceSpans[traceId] && !loadingTraceSpans.has(traceId)) {
      loadTraceSpans(traceId);
    }
    if (tab === 'logs' && !traceDetailLogs[traceId]) {
      loadTraceDetailLogs(traceId);
    }
  };

  // Parse query params from endpoint URL
  const parseQueryParams = (url: string): Record<string, string> => {
    const params: Record<string, string> = {};
    const qIdx = url.indexOf('?');
    if (qIdx === -1) return params;
    const query = url.substring(qIdx + 1);
    query.split('&').forEach(pair => {
      const [key, val] = pair.split('=');
      if (key) params[decodeURIComponent(key)] = val ? decodeURIComponent(val) : '';
    });
    return params;
  };

  // Parse duration from log message (e.g. "(34ms)", "Duration: 120ms")
  const parseDurationFromMessage = (message: string | null): number | null => {
    if (!message) return null;
    const match = message.match(/\((\d+(?:\.\d+)?)ms\)|Duration:\s*(\d+(?:\.\d+)?)ms/i);
    if (match) return parseFloat(match[1] || match[2]);
    return null;
  };

  const getStatusColor = (status: number) => {
    if (status >= 500) return "text-red-400 bg-red-500/10";
    if (status >= 400) return "text-yellow-400 bg-yellow-500/10";
    if (status >= 300) return "text-blue-400 bg-blue-500/10";
    return "text-green-400 bg-green-500/10";
  };

  const getLatencyColor = (latencyMs: number | null) => {
    if (latencyMs === null) return "text-gray-400";
    if (latencyMs < 50) return "text-green-400";
    if (latencyMs < 200) return "text-yellow-400";
    if (latencyMs < 1000) return "text-orange-400";
    return "text-red-400";
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case "GET": return "text-green-400 bg-green-500/10";
      case "POST": return "text-blue-400 bg-blue-500/10";
      case "PUT": return "text-yellow-400 bg-yellow-500/10";
      case "DELETE": return "text-red-400 bg-red-500/10";
      case "PATCH": return "text-purple-400 bg-purple-500/10";
      default: return "text-gray-400 bg-gray-500/10";
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "ERROR": return "bg-red-500/30 text-red-300";
      case "WARNING": case "WARN": return "bg-yellow-500/30 text-yellow-300";
      case "INFO": return "bg-blue-500/30 text-blue-300";
      case "DEBUG": return "bg-gray-500/30 text-gray-300";
      default: return "bg-gray-500/30 text-gray-300";
    }
  };

  const highlightMatch = (text: string, search: string): React.ReactNode => {
    if (!search || !text) return text;
    const idx = text.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-amber-500/40 text-amber-200 font-semibold">{text.slice(idx, idx + search.length)}</span>
        {text.slice(idx + search.length)}
      </>
    );
  };

  // ==================== Trace Analyze Functions ====================

  const ANALYZE_PRESETS = [
    { label: "Service Overview", groupBy: "service", metric: "requests", filters: {} },
    { label: "Slowest Endpoints", groupBy: "endpoint", metric: "p95LatencyMs", filters: {} },
    { label: "Error Hotspots", groupBy: "endpoint", metric: "errors", filters: { minStatus: 400 } },
    { label: "Status Distribution", groupBy: "statusClass", metric: "requests", filters: {} },
    { label: "Latency Distribution", groupBy: "latencyBucket", metric: "requests", filters: {} },
    { label: "Error Trends", groupBy: "service", metric: "requests", filters: { minStatus: 500 } },
  ];

  const GROUPBY_OPTIONS = [
    { value: "service", label: "Service" },
    { value: "endpoint", label: "Endpoint" },
    { value: "method", label: "Method" },
    { value: "status", label: "Status Code" },
    { value: "statusClass", label: "Status Class" },
    { value: "latencyBucket", label: "Latency Bucket" },
    { value: "userAgent", label: "User Agent" },
    { value: "ip", label: "IP" },
  ];

  const METRIC_OPTIONS = [
    { value: "requests", label: "Request Count" },
    { value: "errors", label: "Error Count" },
    { value: "errorRate", label: "Error Rate (%)" },
    { value: "avgLatencyMs", label: "Avg Latency" },
    { value: "p50LatencyMs", label: "P50 Latency" },
    { value: "p95LatencyMs", label: "P95 Latency" },
    { value: "p99LatencyMs", label: "P99 Latency" },
    { value: "maxLatencyMs", label: "Max Latency" },
  ];

  const loadAnalyzeData = async () => {
    if (!user?.accesstoken) return;
    setAnalyzeLoading(true);

    try {
      const data = await SystemStatusService.analyzeTraces(
        user.accesstoken,
        timeRange,
        analyzeGroupBy,
        analyzeFilters,
        {
          timeSeries: analyzeShowTimeSeries,
          interval: getInterval(timeRange.hours || 24),
          limit: 50,
        }
      );
      setAnalyzeData(data);
    } catch (error) {
      console.error("Error loading analyze data:", error);
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const drillDownFromAnalyze = (group: AnalyzeGroup) => {
    const filters: Partial<TraceFilters> = {};
    if (analyzeGroupBy === 'service') filters.service = group.key;
    if (analyzeGroupBy === 'endpoint') filters.endpoint = group.key;
    if (analyzeGroupBy === 'method') filters.method = group.key;
    if (analyzeGroupBy === 'statusClass') {
      filters.minStatus = parseInt(group.key);
      filters.maxStatus = parseInt(group.key) + 99;
    }
    if (analyzeGroupBy === 'status') {
      filters.minStatus = parseInt(group.key);
      filters.maxStatus = parseInt(group.key);
    }
    setTraceFilters(prev => ({ ...prev, ...filters }));
    setTraceView('traces');
    setTimeout(() => loadTraces(false), 100);
  };

  const getMetricValue = (group: AnalyzeGroup, metric: string): number => {
    const val = (group as unknown as Record<string, unknown>)[metric];
    return typeof val === 'number' ? val : 0;
  };

  const formatMetricValue = (value: number, metric: string): string => {
    if (metric === 'errorRate') return `${value.toFixed(2)}%`;
    if (metric.includes('Latency') || metric.includes('latency')) return `${value.toFixed(0)}ms`;
    return value.toLocaleString();
  };

  const renderAnalyzeView = () => {
    return (
      <div className="space-y-4">
        {/* Preset buttons */}
        <div className="flex flex-wrap gap-2">
          {ANALYZE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              onClick={() => {
                setAnalyzeGroupBy(preset.groupBy);
                setAnalyzeMetric(preset.metric);
                setAnalyzeFilters({
                  service: null, endpoint: null, method: null,
                  minStatus: (preset.filters as Record<string, number | null>).minStatus ?? null,
                  maxStatus: (preset.filters as Record<string, number | null>).maxStatus ?? null,
                  minLatencyMs: null, maxLatencyMs: null,
                });
                // Auto-load after setting state
                setTimeout(() => loadAnalyzeData(), 50);
              }}
              className="px-3 py-1 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded text-xs text-gray-300 transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-gray-500 text-xs block mb-1">Group by</label>
              <select
                value={analyzeGroupBy}
                onChange={(e) => setAnalyzeGroupBy(e.target.value)}
                className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {GROUPBY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Metric</label>
              <select
                value={analyzeMetric}
                onChange={(e) => setAnalyzeMetric(e.target.value)}
                className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {METRIC_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Service filter</label>
              <select
                value={analyzeFilters.service || ''}
                onChange={(e) => setAnalyzeFilters(prev => ({ ...prev, service: e.target.value || null }))}
                className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All</option>
                {services?.services.map(s => (
                  <option key={s.name} value={s.name}>{s.displayName}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-gray-500 text-xs">
                <input
                  type="checkbox"
                  checked={analyzeShowTimeSeries}
                  onChange={(e) => setAnalyzeShowTimeSeries(e.target.checked)}
                  className="mr-1"
                />
                Time series
              </label>
            </div>
            <button
              onClick={loadAnalyzeData}
              disabled={analyzeLoading}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white text-xs font-medium disabled:opacity-50"
            >
              {analyzeLoading ? "Loading..." : "Analyze"}
            </button>
          </div>
        </div>

        {/* Results */}
        {analyzeData && analyzeData.groups.length > 0 && (
          <>
            {/* Bar Chart */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-gray-400 text-xs font-medium mb-3">
                {METRIC_OPTIONS.find(o => o.value === analyzeMetric)?.label || analyzeMetric} by {GROUPBY_OPTIONS.find(o => o.value === analyzeGroupBy)?.label || analyzeGroupBy}
              </h4>
              <div className="space-y-2">
                {(() => {
                  const maxValue = Math.max(...analyzeData.groups.map(g => getMetricValue(g, analyzeMetric)));
                  return analyzeData.groups.map((group) => {
                    const value = getMetricValue(group, analyzeMetric);
                    const widthPct = maxValue > 0 ? (value / maxValue) * 100 : 0;
                    const isError = analyzeMetric === 'errors' || analyzeMetric === 'errorRate';
                    const barColor = isError ? 'bg-red-500' : 'bg-blue-500';

                    return (
                      <div
                        key={group.key}
                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-750 rounded p-1 transition-colors"
                        onClick={() => drillDownFromAnalyze(group)}
                        title="Click to drill down to traces"
                      >
                        <div className="w-32 text-xs text-gray-300 font-mono truncate" title={group.key}>
                          {group.key === 'default' ? 'classic' : group.key}
                        </div>
                        <div className="flex-1 bg-gray-700 rounded-full h-4 overflow-hidden">
                          <div
                            className={`h-full ${barColor} opacity-70 rounded-full transition-all`}
                            style={{ width: `${Math.max(widthPct, 1)}%` }}
                          />
                        </div>
                        <div className="w-20 text-xs text-gray-300 text-right font-mono">
                          {formatMetricValue(value, analyzeMetric)}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Time Series Chart */}
            {analyzeShowTimeSeries && analyzeData.timeSeries && analyzeData.timeSeries.length > 0 && (
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-gray-400 text-xs font-medium mb-3">Time Series</h4>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analyzeData.timeSeries.map(point => {
                      const flat: Record<string, unknown> = { timestamp: point.timestamp };
                      Object.entries(point.groups).forEach(([key, metrics]) => {
                        flat[key] = (metrics as Record<string, unknown>)[analyzeMetric === 'errorRate' ? 'errors' : analyzeMetric] ?? (metrics as Record<string, unknown>).requests ?? 0;
                      });
                      return flat;
                    })}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(val: number) => {
                          const d = new Date(val * 1000);
                          return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
                        }}
                        stroke="#6B7280"
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '6px', fontSize: '11px' }}
                        labelFormatter={(val) => new Date(Number(val) * 1000).toLocaleString()}
                      />
                      {analyzeData.groups.slice(0, 6).map((group, idx) => {
                        const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];
                        return (
                          <Area
                            key={group.key}
                            type="monotone"
                            dataKey={group.key}
                            name={group.key === 'default' ? 'classic' : group.key}
                            stackId="1"
                            fill={colors[idx % colors.length]}
                            stroke={colors[idx % colors.length]}
                            fillOpacity={0.3}
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Data Table */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-700 text-gray-500">
                      <th className="text-left px-4 py-2 font-medium">Group</th>
                      <th className="text-right px-3 py-2 font-medium">Requests</th>
                      <th className="text-right px-3 py-2 font-medium">Errors</th>
                      <th className="text-right px-3 py-2 font-medium">Err%</th>
                      <th className="text-right px-3 py-2 font-medium">Avg</th>
                      <th className="text-right px-3 py-2 font-medium">P50</th>
                      <th className="text-right px-3 py-2 font-medium">P95</th>
                      <th className="text-right px-3 py-2 font-medium">P99</th>
                      <th className="text-right px-3 py-2 font-medium">Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyzeData.groups.map((group) => (
                      <tr
                        key={group.key}
                        className="border-b border-gray-700/50 hover:bg-gray-750 cursor-pointer transition-colors"
                        onClick={() => drillDownFromAnalyze(group)}
                        title="Click to drill down"
                      >
                        <td className="px-4 py-2 text-gray-300 font-mono truncate max-w-[200px]" title={group.key}>
                          {group.key === 'default' ? 'classic' : group.key}
                        </td>
                        <td className="text-right px-3 py-2 text-white">{group.requests.toLocaleString()}</td>
                        <td className={`text-right px-3 py-2 ${group.errors > 0 ? 'text-red-400' : 'text-gray-400'}`}>
                          {group.errors}
                        </td>
                        <td className={`text-right px-3 py-2 ${group.errorRate > 1 ? 'text-red-400' : group.errorRate > 0.5 ? 'text-yellow-400' : 'text-gray-400'}`}>
                          {group.errorRate.toFixed(2)}%
                        </td>
                        <td className="text-right px-3 py-2 text-gray-300 font-mono">
                          {group.avgLatencyMs !== null ? `${group.avgLatencyMs.toFixed(0)}ms` : '-'}
                        </td>
                        <td className="text-right px-3 py-2 text-gray-300 font-mono">
                          {group.p50LatencyMs !== null ? `${group.p50LatencyMs.toFixed(0)}ms` : '-'}
                        </td>
                        <td className="text-right px-3 py-2 text-gray-300 font-mono">
                          {group.p95LatencyMs !== null ? `${group.p95LatencyMs.toFixed(0)}ms` : '-'}
                        </td>
                        <td className="text-right px-3 py-2 text-gray-300 font-mono">
                          {group.p99LatencyMs !== null ? `${group.p99LatencyMs.toFixed(0)}ms` : '-'}
                        </td>
                        <td className={`text-right px-3 py-2 font-mono ${getLatencyColor(group.maxLatencyMs)}`}>
                          {group.maxLatencyMs !== null ? `${group.maxLatencyMs.toFixed(0)}ms` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 text-[10px] text-gray-600 border-t border-gray-700">
                Click any row to drill down to individual traces
              </div>
            </div>
          </>
        )}

        {/* Empty / Loading states */}
        {analyzeLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-400 text-sm">Analyzing traces...</span>
          </div>
        )}
        {!analyzeLoading && !analyzeData && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">Select a preset or configure grouping and click "Analyze"</p>
          </div>
        )}
        {!analyzeLoading && analyzeData && analyzeData.groups.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">No data found for the selected filters</p>
          </div>
        )}
      </div>
    );
  };

  const renderTraceExplorer = () => {
    return (
      <div className="space-y-4">
        {/* Filter Bar */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-gray-500 text-xs block mb-1">Service</label>
              <select
                value={traceFilters.service || ''}
                onChange={(e) => setTraceFilters(prev => ({ ...prev, service: e.target.value || null }))}
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Services</option>
                {services?.services.map(s => (
                  <option key={s.name} value={s.name}>{s.displayName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Method</label>
              <select
                value={traceFilters.method || ''}
                onChange={(e) => setTraceFilters(prev => ({ ...prev, method: e.target.value || null }))}
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Status</label>
              <select
                value={traceFilters.minStatus !== null ? `${traceFilters.minStatus}-${traceFilters.maxStatus || ''}` : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) {
                    setTraceFilters(prev => ({ ...prev, minStatus: null, maxStatus: null }));
                  } else if (val === '500-') {
                    setTraceFilters(prev => ({ ...prev, minStatus: 500, maxStatus: null }));
                  } else if (val === '400-500') {
                    setTraceFilters(prev => ({ ...prev, minStatus: 400, maxStatus: 500 }));
                  } else if (val === '200-300') {
                    setTraceFilters(prev => ({ ...prev, minStatus: 200, maxStatus: 300 }));
                  }
                }}
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="200-300">2xx Success</option>
                <option value="400-500">4xx Client Error</option>
                <option value="500-">5xx Server Error</option>
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Min Latency (ms)</label>
              <input
                type="number"
                value={traceFilters.minLatencyMs ?? ''}
                onChange={(e) => setTraceFilters(prev => ({ ...prev, minLatencyMs: e.target.value ? Number(e.target.value) : null }))}
                placeholder="0"
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Endpoint</label>
              <input
                type="text"
                value={traceFilters.endpoint || ''}
                onChange={(e) => setTraceFilters(prev => ({ ...prev, endpoint: e.target.value || null }))}
                placeholder="/session/ (prefix match)"
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Trace ID</label>
              <input
                type="text"
                value={traceFilters.traceId || ''}
                onChange={(e) => setTraceFilters(prev => ({ ...prev, traceId: e.target.value || null }))}
                placeholder="abc123..."
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          {/* Row 2: Search & User */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="text-gray-500 text-xs block mb-1">Search Logs</label>
              <input
                type="text"
                value={traceFilters.textSearch || ''}
                onChange={(e) => setTraceFilters(prev => ({ ...prev, textSearch: e.target.value || null }))}
                placeholder="NullPointerException, error message..."
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {traceFilters.textSearch && traceFilters.textSearch.length > 0 && traceFilters.textSearch.length < 3 && (
                <span className="text-yellow-500 text-[10px] mt-0.5 block">Min 3 characters</span>
              )}
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">User (email or ID)</label>
              <input
                type="text"
                value={traceFilters.userId || ''}
                onChange={(e) => setTraceFilters(prev => ({ ...prev, userId: e.target.value || null }))}
                placeholder="john@example.com or 12345"
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={() => {
                  setTraceFilters(prev => ({ ...prev, minStatus: 500, maxStatus: null }));
                  setTimeout(() => loadTraces(false), 100);
                }}
                className="px-2 py-1.5 bg-red-900/40 hover:bg-red-900/60 border border-red-700/50 rounded text-red-300 text-[10px] font-medium"
              >
                Errors only
              </button>
              <button
                onClick={() => {
                  setTraceFilters(prev => ({ ...prev, minLatencyMs: 500, maxLatencyMs: null }));
                  setTimeout(() => loadTraces(false), 100);
                }}
                className="px-2 py-1.5 bg-yellow-900/40 hover:bg-yellow-900/60 border border-yellow-700/50 rounded text-yellow-300 text-[10px] font-medium"
              >
                Slow &gt;500ms
              </button>
              <button
                onClick={() => {
                  setTraceFilters(prev => ({ ...prev, minStatus: 400, maxStatus: 500 }));
                  setTimeout(() => loadTraces(false), 100);
                }}
                className="px-2 py-1.5 bg-orange-900/40 hover:bg-orange-900/60 border border-orange-700/50 rounded text-orange-300 text-[10px] font-medium"
              >
                4xx
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={() => loadTraces(false)}
              disabled={tracesLoading}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white text-xs font-medium disabled:opacity-50"
            >
              {tracesLoading ? "Searching..." : "Search Traces"}
            </button>
            <button
              onClick={() => {
                setTraceFilters({
                  service: null, endpoint: null, method: null,
                  minStatus: null, maxStatus: null,
                  minLatencyMs: null, maxLatencyMs: null,
                  traceId: null, textSearch: null, userId: null,
                });
                setTraces([]);
                setTracesOffset(0);
                setTracesHasMore(false);
                setUserTraceInfo(null);
                setUserDeviceFilter(null);
              }}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 text-xs"
            >
              Clear
            </button>
            {traces.length > 0 && (
              <span className="text-gray-500 text-xs">
                Showing {traces.length} trace{traces.length !== 1 ? 's' : ''}
                {tracesHasMore && ' (more available)'}
              </span>
            )}
          </div>
        </div>

        {/* User Info Panel (when in user-scoped search mode) */}
        {userTraceInfo && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {(userTraceInfo.user.name || userTraceInfo.user.email).charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-white text-sm font-medium">
                  {userTraceInfo.user.name || 'Unknown'} <span className="text-gray-400 font-normal">({userTraceInfo.user.email})</span>
                </div>
                <div className="text-gray-500 text-xs">User ID: {userTraceInfo.user.id}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setUserDeviceFilter(null); setTimeout(() => loadTraces(false), 100); }}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  userDeviceFilter === null ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                All Devices
              </button>
              {userTraceInfo.devices.map(device => (
                <button
                  key={device.id}
                  onClick={() => { setUserDeviceFilter(device.id); setTimeout(() => loadTraces(false), 100); }}
                  className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                    userDeviceFilter === device.id ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {device.model || 'Unknown'} ({device.os || '?'}) - {device.accessToken}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Trace List */}
        {traces.length > 0 && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {/* Header - sortable columns */}
            <div className="grid grid-cols-[40px_90px_100px_1fr_60px_60px_80px] gap-2 px-4 py-2 text-xs text-gray-500 font-medium border-b border-gray-700">
              <div></div>
              <div
                className="cursor-pointer hover:text-gray-300 select-none flex items-center gap-1"
                onClick={() => {
                  const newOrder = traceSortBy === 'timestamp' && traceSortOrder === 'desc' ? 'asc' : 'desc';
                  setTraceSortBy('timestamp');
                  setTraceSortOrder(newOrder);
                  setTimeout(() => loadTraces(false), 100);
                }}
              >
                Time {traceSortBy === 'timestamp' && (traceSortOrder === 'desc' ? '\u2193' : '\u2191')}
              </div>
              <div>Service</div>
              <div>Endpoint</div>
              <div>Method</div>
              <div
                className="cursor-pointer hover:text-gray-300 select-none flex items-center gap-1"
                onClick={() => {
                  const newOrder = traceSortBy === 'status' && traceSortOrder === 'desc' ? 'asc' : 'desc';
                  setTraceSortBy('status');
                  setTraceSortOrder(newOrder);
                  setTimeout(() => loadTraces(false), 100);
                }}
              >
                Status {traceSortBy === 'status' && (traceSortOrder === 'desc' ? '\u2193' : '\u2191')}
              </div>
              <div
                className="text-right cursor-pointer hover:text-gray-300 select-none flex items-center justify-end gap-1"
                onClick={() => {
                  const newOrder = traceSortBy === 'latencyMs' && traceSortOrder === 'desc' ? 'asc' : 'desc';
                  setTraceSortBy('latencyMs');
                  setTraceSortOrder(newOrder);
                  setTimeout(() => loadTraces(false), 100);
                }}
              >
                Latency {traceSortBy === 'latencyMs' && (traceSortOrder === 'desc' ? '\u2193' : '\u2191')}
              </div>
            </div>

            {/* Rows */}
            {traces.map((trace, traceIndex) => {
              const isExpanded = expandedTraceDetails.has(trace.traceId);
              const isFocused = focusedTraceIndex === traceIndex;
              const traceTime = new Date(trace.timestamp > 1e12 ? trace.timestamp : trace.timestamp * 1000);
              const timeStr = traceTime.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

              return (
                <div key={`${trace.traceId}-${trace.timestamp}`}>
                  {/* Trace Row */}
                  <div
                    className={`grid grid-cols-[40px_90px_100px_1fr_60px_60px_80px] gap-2 px-4 py-2 text-xs cursor-pointer hover:bg-gray-750 transition-colors ${
                      isExpanded ? 'bg-gray-750 border-l-2 border-blue-500' : isFocused ? 'bg-gray-750/50 border-l-2 border-gray-400' : 'border-l-2 border-transparent'
                    }`}
                    onClick={() => { setFocusedTraceIndex(traceIndex); toggleTraceDetail(trace.traceId); }}
                  >
                    <div className="flex items-center">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                    <div className="text-gray-300 font-mono">{timeStr}</div>
                    <div>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-700 text-gray-300 truncate block">
                        {trace.service === 'default' ? 'classic' : trace.service}
                      </span>
                    </div>
                    <div className="text-gray-300 font-mono truncate" title={trace.endpoint}>
                      {trace.endpoint}
                    </div>
                    <div>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getMethodColor(trace.method)}`}>
                        {trace.method}
                      </span>
                    </div>
                    <div>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(trace.status)}`}>
                        {trace.status}
                      </span>
                    </div>
                    <div className={`text-right font-mono ${getLatencyColor(trace.latencyMs)}`}>
                      {trace.latencyMs !== null ? `${trace.latencyMs.toFixed(0)}ms` : 'N/A'}
                    </div>
                  </div>

                  {/* Matched log preview (from text search) */}
                  {trace.matchedLog && (
                    <div className="px-4 pl-14 pb-1 -mt-1">
                      <div className="text-[10px] text-amber-400/80 bg-amber-900/20 border border-amber-700/30 rounded px-2 py-1 font-mono truncate">
                        <span className={`mr-1 ${getSeverityColor(trace.matchedLog.severity)}`}>[{trace.matchedLog.severity}]</span>
                        {traceFilters.textSearch ? highlightMatch(trace.matchedLog.message, traceFilters.textSearch) : trace.matchedLog.message}
                      </div>
                    </div>
                  )}

                  {/* Device badge (from user search) */}
                  {userTraceInfo && (trace as UserTraceEntry).device && (
                    <div className="px-4 pl-14 pb-1 -mt-1">
                      <span className="text-[10px] text-blue-300 bg-blue-900/30 border border-blue-700/30 rounded px-1.5 py-0.5">
                        {(trace as UserTraceEntry).device?.model || 'Unknown'} ({(trace as UserTraceEntry).device?.os || '?'})
                      </span>
                    </div>
                  )}

                  {/* Expanded Detail with Tabs */}
                  {isExpanded && (() => {
                    const activeDetailTab = traceDetailTab[trace.traceId] || 'info';
                    const detailTabs: { id: TraceDetailTab; label: string }[] = [
                      { id: 'info', label: 'Request Info' },
                      { id: 'waterfall', label: 'Waterfall' },
                      { id: 'logs', label: 'Logs' },
                      { id: 'http', label: 'HTTP Detail' },
                    ];

                    return (
                      <div className="bg-gray-900/50 border-t border-gray-700 border-l-2 border-blue-500">
                        {/* Detail Tab Bar */}
                        <div className="flex border-b border-gray-700 px-4">
                          {detailTabs.map(tab => (
                            <button
                              key={tab.id}
                              onClick={(e) => { e.stopPropagation(); switchTraceDetailTab(trace.traceId, tab.id); }}
                              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                                activeDetailTab === tab.id
                                  ? 'border-blue-500 text-blue-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-300'
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        <div className="px-4 py-3">
                          {/* Tab 1: Request Info */}
                          {activeDetailTab === 'info' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div>
                                <span className="text-gray-500 text-[10px] block">Trace ID</span>
                                <span className="text-gray-300 text-xs font-mono break-all">{trace.traceId}</span>
                              </div>
                              {trace.requestId && (
                                <div>
                                  <span className="text-gray-500 text-[10px] block">Request ID</span>
                                  <span className="text-gray-300 text-xs font-mono">{trace.requestId}</span>
                                </div>
                              )}
                              <div>
                                <span className="text-gray-500 text-[10px] block">Service</span>
                                <span className="text-gray-300 text-xs">{trace.service === 'default' ? 'Classic Backend' : trace.service}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 text-[10px] block">Endpoint</span>
                                <span className="text-gray-300 text-xs font-mono break-all">{trace.endpoint}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 text-[10px] block">Method</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getMethodColor(trace.method)}`}>{trace.method}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 text-[10px] block">Status</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(trace.status)}`}>{trace.status}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 text-[10px] block">Latency</span>
                                <span className={`text-xs font-mono ${getLatencyColor(trace.latencyMs)}`}>
                                  {trace.latencyMs !== null ? `${trace.latencyMs.toFixed(2)}ms` : 'N/A'}
                                </span>
                              </div>
                              {trace.ip && (
                                <div>
                                  <span className="text-gray-500 text-[10px] block">IP</span>
                                  <span className="text-gray-300 text-xs font-mono">{trace.ip}</span>
                                </div>
                              )}
                              {trace.userAgent && (
                                <div className="col-span-2">
                                  <span className="text-gray-500 text-[10px] block">User Agent</span>
                                  <span className="text-gray-300 text-xs break-all">{trace.userAgent}</span>
                                </div>
                              )}
                              {trace.responseSize !== null && (
                                <div>
                                  <span className="text-gray-500 text-[10px] block">Response Size</span>
                                  <span className="text-gray-300 text-xs">
                                    {trace.responseSize > 1024 ? `${(trace.responseSize / 1024).toFixed(1)} KB` : `${trace.responseSize} B`}
                                  </span>
                                </div>
                              )}
                              {trace.host && (
                                <div>
                                  <span className="text-gray-500 text-[10px] block">Host</span>
                                  <span className="text-gray-300 text-xs font-mono">{trace.host}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Tab 2: Waterfall */}
                          {activeDetailTab === 'waterfall' && (() => {
                            const spans = traceSpans[trace.traceId];
                            const logs = traceDetailLogs[trace.traceId] || [];
                            const isLoadingSpans = loadingTraceSpans.has(trace.traceId);

                            if (isLoadingSpans && !spans) {
                              return (
                                <div className="flex items-center gap-2 text-gray-500 text-xs py-4">
                                  <div className="w-3 h-3 border border-gray-500 border-t-blue-400 rounded-full animate-spin"></div>
                                  Loading waterfall data...
                                </div>
                              );
                            }

                            if (!spans || spans.length === 0) {
                              return <p className="text-gray-600 text-xs py-4">No span data available for this trace.</p>;
                            }

                            // Calculate timeline boundaries
                            const traceStart = Math.min(...spans.map(s => s.startTimestamp));
                            const traceEnd = Math.max(...spans.map(s => s.endTimestamp));
                            const totalDuration = traceEnd - traceStart;
                            const startTime = new Date(traceStart);
                            const endTime = new Date(traceEnd);

                            return (
                              <div className="space-y-3">
                                {/* Timeline header */}
                                <div className="flex justify-between text-[10px] text-gray-500 font-mono">
                                  <span>{startTime.toLocaleTimeString('en-US', { hour12: false, fractionalSecondDigits: 3 } as Intl.DateTimeFormatOptions)}</span>
                                  <span>{totalDuration.toFixed(0)}ms total</span>
                                  <span>{endTime.toLocaleTimeString('en-US', { hour12: false, fractionalSecondDigits: 3 } as Intl.DateTimeFormatOptions)}</span>
                                </div>

                                {/* Spans */}
                                <div className="space-y-2">
                                  {spans.map((span, idx) => {
                                    const offsetPct = totalDuration > 0 ? ((span.startTimestamp - traceStart) / totalDuration) * 100 : 0;
                                    const widthPct = totalDuration > 0 ? (span.latencyMs / totalDuration) * 100 : 100;
                                    const serviceColors: Record<string, string> = {
                                      'default': 'bg-blue-500',
                                      'surfr-wrapped': 'bg-purple-500',
                                      'surfr-discover': 'bg-green-500',
                                      'surfr-spot-dashboard': 'bg-orange-500',
                                    };
                                    const barColor = serviceColors[span.service] || 'bg-cyan-500';

                                    // Find logs within this span's time range
                                    const spanLogs = logs.filter(log => {
                                      const logTs = new Date(log.timestamp).getTime();
                                      return logTs >= span.startTimestamp && logTs <= span.endTimestamp;
                                    });

                                    return (
                                      <div key={`${span.requestId}-${idx}`} className="relative">
                                        {/* Span label */}
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-700 text-gray-300">
                                            {span.service === 'default' ? 'classic' : span.service}
                                          </span>
                                          <span className="text-gray-400 text-[10px] font-mono truncate">{span.endpoint}</span>
                                          <span className={`px-1 rounded text-[10px] font-medium ${getMethodColor(span.method)}`}>{span.method}</span>
                                          <span className={`px-1 rounded text-[10px] font-medium ${getStatusColor(span.status)}`}>{span.status}</span>
                                          <span className={`text-[10px] font-mono ${getLatencyColor(span.latencyMs)}`}>{span.latencyMs.toFixed(0)}ms</span>
                                        </div>

                                        {/* Timeline bar */}
                                        <div className="relative h-6 bg-gray-800 rounded overflow-hidden">
                                          {/* Span bar */}
                                          <div
                                            className={`absolute top-0 h-full ${barColor} opacity-60 rounded`}
                                            style={{ left: `${offsetPct}%`, width: `${Math.max(widthPct, 0.5)}%` }}
                                          />

                                          {/* Log event markers */}
                                          {spanLogs.map((log, logIdx) => {
                                            const logTs = new Date(log.timestamp).getTime();
                                            const logOffsetPct = totalDuration > 0 ? ((logTs - traceStart) / totalDuration) * 100 : 0;
                                            const markerColor = log.severity === 'ERROR' ? 'bg-red-400' :
                                              log.severity === 'WARNING' || log.severity === 'WARN' ? 'bg-yellow-400' :
                                              log.severity === 'DEBUG' ? 'bg-gray-400' : 'bg-blue-400';
                                            const duration = parseDurationFromMessage(log.message);

                                            return (
                                              <div key={logIdx} className="absolute top-0 h-full" style={{ left: `${logOffsetPct}%` }}>
                                                {duration && totalDuration > 0 ? (
                                                  <div
                                                    className={`h-full ${markerColor} opacity-40 rounded-sm`}
                                                    style={{ width: `${(duration / totalDuration) * 100}%`, minWidth: '2px' }}
                                                    title={`${log.severity}: ${log.message?.substring(0, 100)} (${duration}ms)`}
                                                  />
                                                ) : (
                                                  <div
                                                    className={`w-1.5 h-full ${markerColor} opacity-80 rounded-sm`}
                                                    title={`${log.severity}: ${log.message?.substring(0, 100)}`}
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Legend */}
                                <div className="flex items-center gap-4 text-[10px] text-gray-500 pt-2 border-t border-gray-800">
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400 rounded-sm inline-block"></span> INFO</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-400 rounded-sm inline-block"></span> WARN</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-sm inline-block"></span> ERROR</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-400 rounded-sm inline-block"></span> DEBUG</span>
                                  <span className="ml-auto">{spans.length} span{spans.length !== 1 ? 's' : ''}</span>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Tab 3: Logs */}
                          {activeDetailTab === 'logs' && (
                            <div>
                              {loadingTraceLogIds.has(trace.traceId) && !traceDetailLogs[trace.traceId] && (
                                <div className="flex items-center gap-2 text-gray-500 text-xs py-2">
                                  <div className="w-3 h-3 border border-gray-500 border-t-blue-400 rounded-full animate-spin"></div>
                                  Loading logs...
                                </div>
                              )}

                              {traceDetailLogs[trace.traceId] && traceDetailLogs[trace.traceId].length === 0 && (
                                <p className="text-gray-600 text-xs py-2">No application logs found for this trace.</p>
                              )}

                              {traceDetailLogs[trace.traceId] && traceDetailLogs[trace.traceId].length > 0 && (
                                <div className="space-y-1">
                                  <div className="text-gray-500 text-[10px] mb-2">
                                    {traceDetailLogs[trace.traceId].length} log entries loaded
                                  </div>
                                  {traceDetailLogs[trace.traceId].map((log, idx) => {
                                    const logTime = new Date(log.timestamp);
                                    const logTimeStr = logTime.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 } as Intl.DateTimeFormatOptions);

                                    return (
                                      <div key={idx} className={`text-xs font-mono p-2 rounded ${
                                        log.severity === 'ERROR' ? 'bg-red-500/10 border-l-2 border-red-500' :
                                        log.severity === 'WARNING' || log.severity === 'WARN' ? 'bg-yellow-500/10 border-l-2 border-yellow-500' :
                                        'bg-gray-800/50'
                                      }`}>
                                        <div className="flex items-start gap-2">
                                          <span className={`px-1 rounded text-[10px] font-medium ${getSeverityColor(log.severity)}`}>
                                            {log.severity}
                                          </span>
                                          <span className="text-gray-500">{logTimeStr}</span>
                                          {log.sourceLocation?.file && (
                                            <span className="text-gray-600">
                                              {log.sourceLocation.file}{log.sourceLocation.line ? `:${log.sourceLocation.line}` : ''}
                                            </span>
                                          )}
                                        </div>
                                        {log.message && (
                                          <div className="text-gray-300 mt-1 ml-6 break-all max-h-24 overflow-auto">
                                            {log.message}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}

                                  {traceLogHasMore[trace.traceId] && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); loadTraceDetailLogs(trace.traceId, true); }}
                                      disabled={loadingTraceLogIds.has(trace.traceId)}
                                      className="text-blue-400 hover:text-blue-300 text-xs mt-2 flex items-center gap-1"
                                    >
                                      {loadingTraceLogIds.has(trace.traceId) ? (
                                        <>
                                          <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                          Loading...
                                        </>
                                      ) : (
                                        'Load more logs...'
                                      )}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Tab 4: HTTP Detail */}
                          {activeDetailTab === 'http' && (() => {
                            const queryParams = parseQueryParams(trace.endpoint);
                            const basePath = trace.endpoint.split('?')[0];
                            const hasQueryParams = Object.keys(queryParams).length > 0;

                            return (
                              <div className="space-y-4">
                                {/* Request section */}
                                <div>
                                  <h4 className="text-gray-400 text-xs font-medium mb-2">Request</h4>
                                  <div className="bg-gray-800 rounded p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getMethodColor(trace.method)}`}>
                                        {trace.method}
                                      </span>
                                      <span className="text-gray-300 text-xs font-mono break-all">{basePath}</span>
                                    </div>

                                    {/* Query Parameters */}
                                    {hasQueryParams && (
                                      <div className="mt-3 pt-3 border-t border-gray-700">
                                        <h5 className="text-gray-500 text-[10px] font-medium mb-1">Query Parameters</h5>
                                        <div className="space-y-1">
                                          {Object.entries(queryParams).map(([key, val]) => (
                                            <div key={key} className="flex gap-2 text-xs">
                                              <span className="text-blue-400 font-mono">{key}:</span>
                                              <span className="text-gray-300 font-mono break-all">{val}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Request metadata */}
                                    <div className="mt-3 pt-3 border-t border-gray-700 space-y-1">
                                      {trace.ip && (
                                        <div className="flex gap-2 text-xs">
                                          <span className="text-gray-500">IP:</span>
                                          <span className="text-gray-300 font-mono">{trace.ip}</span>
                                        </div>
                                      )}
                                      {trace.userAgent && (
                                        <div className="flex gap-2 text-xs">
                                          <span className="text-gray-500">User-Agent:</span>
                                          <span className="text-gray-300 break-all">{trace.userAgent}</span>
                                        </div>
                                      )}
                                      {trace.host && (
                                        <div className="flex gap-2 text-xs">
                                          <span className="text-gray-500">Host:</span>
                                          <span className="text-gray-300 font-mono">{trace.host}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Response section */}
                                <div>
                                  <h4 className="text-gray-400 text-xs font-medium mb-2">Response</h4>
                                  <div className="bg-gray-800 rounded p-3 space-y-2">
                                    <div className="flex gap-2 text-xs">
                                      <span className="text-gray-500">Status:</span>
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(trace.status)}`}>
                                        {trace.status}
                                      </span>
                                    </div>
                                    <div className="flex gap-2 text-xs">
                                      <span className="text-gray-500">Latency:</span>
                                      <span className={`font-mono ${getLatencyColor(trace.latencyMs)}`}>
                                        {trace.latencyMs !== null ? `${trace.latencyMs.toFixed(2)}ms` : 'N/A'}
                                      </span>
                                    </div>
                                    {trace.responseSize !== null && (
                                      <div className="flex gap-2 text-xs">
                                        <span className="text-gray-500">Response Size:</span>
                                        <span className="text-gray-300 font-mono">
                                          {trace.responseSize > 1024 ? `${(trace.responseSize / 1024).toFixed(1)} KB` : `${trace.responseSize} B`}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Timing section */}
                                <div>
                                  <h4 className="text-gray-400 text-xs font-medium mb-2">Timing</h4>
                                  <div className="bg-gray-800 rounded p-3 space-y-2">
                                    <div className="flex gap-2 text-xs">
                                      <span className="text-gray-500">Timestamp:</span>
                                      <span className="text-gray-300 font-mono">
                                        {new Date(trace.timestamp > 1e12 ? trace.timestamp : trace.timestamp * 1000).toISOString()}
                                      </span>
                                    </div>
                                    <div className="flex gap-2 text-xs">
                                      <span className="text-gray-500">Trace ID:</span>
                                      <span className="text-gray-300 font-mono break-all">{trace.traceId}</span>
                                    </div>
                                    {trace.requestId && (
                                      <div className="flex gap-2 text-xs">
                                        <span className="text-gray-500">Request ID:</span>
                                        <span className="text-gray-300 font-mono">{trace.requestId}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}

            {/* Load More Traces */}
            {tracesHasMore && (
              <div className="px-4 py-3 border-t border-gray-700">
                <button
                  onClick={() => loadTraces(true)}
                  disabled={tracesLoading}
                  className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                >
                  {tracesLoading ? (
                    <>
                      <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </>
                  ) : (
                    'Load more traces...'
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading state */}
        {traces.length === 0 && tracesLoading && (
          <div className="flex items-center justify-center py-12 gap-2">
            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-400 text-sm">Searching traces...</span>
          </div>
        )}

        {/* Empty state */}
        {traces.length === 0 && !tracesLoading && (
          <div className="text-center py-12 bg-gray-800/50 rounded-lg">
            {(traceFilters.textSearch || traceFilters.userId || traceFilters.service || traceFilters.traceId || traceFilters.endpoint || traceFilters.minStatus !== null) ? (
              <>
                <ExclamationCircleIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400 text-sm font-medium">No traces found</p>
                <p className="text-gray-500 text-xs mt-1">Try adjusting your filters or expanding the time range</p>
              </>
            ) : (
              <>
                <ServerIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400 text-sm font-medium">Trace Explorer</p>
                <p className="text-gray-500 text-xs mt-1">Use the filters above and click &quot;Search Traces&quot; to explore request traces</p>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderServicesTab = () => {
    return (
      <div className="space-y-4">
        {/* Sub-tabs: Services | Trace Explorer | Analyze */}
        <div className="flex gap-1 bg-gray-800 rounded-lg p-1 w-fit">
          <button
            onClick={() => setTraceView('services')}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              traceView === 'services'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Services
          </button>
          <button
            onClick={() => {
              setTraceView('traces');
              if (traces.length === 0 && !tracesLoading) {
                setTimeout(() => loadTraces(false), 100);
              }
            }}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              traceView === 'traces'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Trace Explorer
          </button>
          <button
            onClick={() => setTraceView('analyze')}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              traceView === 'analyze'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Analyze
          </button>
        </div>

        {traceView === 'traces' ? renderTraceExplorer() : traceView === 'analyze' ? renderAnalyzeView() : renderServicesList()}
      </div>
    );
  };

  const renderServicesList = () => {
    if (!services) return <div className="text-gray-400">No data available</div>;

    return (
      <div className="space-y-4">
        {services.note && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p className="text-blue-300 text-sm">{services.note}</p>
          </div>
        )}

        <div className="grid gap-4">
          {services.services.map((service) => {
            const isExpanded = expandedServices.has(service.name);

            return (
              <div key={service.name} className="bg-gray-800 rounded-lg overflow-hidden">
                {/* Service Header - Clickable */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-750 transition-colors"
                  onClick={() => toggleServiceExpanded(service.name)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                      )}
                      <ServerIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <h3 className="text-white font-semibold">{service.displayName}</h3>
                        <p className="text-gray-500 text-sm">{service.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(service.status)}`}>
                        {service.status}
                      </span>
                    </div>
                  </div>

                  {/* Traffic Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-sm ml-8">
                    <div>
                      <span className="text-gray-500">Requests</span>
                      <p className="text-white font-medium">{service.metrics.requests?.toLocaleString() ?? "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Errors</span>
                      <p className={`font-medium ${(service.metrics.errors ?? 0) > 0 ? "text-red-400" : "text-white"}`}>
                        {service.metrics.errors ?? "0"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Error Rate</span>
                      <p className={`font-medium ${
                        (service.metrics.errorRate ?? 0) > 1 ? "text-red-400" :
                        (service.metrics.errorRate ?? 0) > 0.5 ? "text-yellow-400" : "text-green-400"
                      }`}>
                        {service.metrics.errorRate != null ? `${service.metrics.errorRate}%` : "0%"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Avg Latency</span>
                      <p className="text-white font-medium">{service.metrics.avgLatencyMs ? `${service.metrics.avgLatencyMs.toFixed(0)}ms` : "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">P95 Latency</span>
                      <p className="text-white font-medium">{service.metrics.p95LatencyMs ? `${service.metrics.p95LatencyMs.toFixed(0)}ms` : "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">P99 Latency</span>
                      <p className="text-white font-medium">{service.metrics.p99LatencyMs ? `${service.metrics.p99LatencyMs.toFixed(0)}ms` : "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Max Latency</span>
                      <p className={`font-medium ${(service.metrics.maxLatencyMs ?? 0) > 10000 ? "text-yellow-400" : "text-white"}`}>
                        {service.metrics.maxLatencyMs ? `${service.metrics.maxLatencyMs.toFixed(0)}ms` : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Resource Metrics */}
                  {service.resources && (
                    <div className="grid grid-cols-3 gap-4 text-sm mt-3 pt-3 border-t border-gray-700 ml-8">
                      <div>
                        <span className="text-gray-500">Instances</span>
                        <p className="text-white">{service.resources.instanceCount ?? "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">CPU</span>
                        <p className="text-white">{service.resources.cpuUsage != null ? `${service.resources.cpuUsage}%` : "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Memory</span>
                        <p className="text-white">{service.resources.memoryUsageMB != null ? `${service.resources.memoryUsageMB} MB` : "N/A"}</p>
                      </div>
                    </div>
                  )}

                  {/* Endpoint Metrics Toggle */}
                  <div className="mt-3 pt-3 border-t border-gray-700 ml-8">
                    <button
                      onClick={(e) => toggleEndpoints(service.name, e)}
                      className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {expandedEndpoints.has(service.name) ? (
                        <ChevronDownIcon className="w-4 h-4" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4" />
                      )}
                      <ChartBarIcon className="w-4 h-4" />
                      <span>View Endpoint Metrics</span>
                      {loadingEndpoints.has(service.name) && (
                        <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                      )}
                    </button>

                    {/* Endpoint Metrics Table */}
                    {expandedEndpoints.has(service.name) && renderEndpointMetrics(service.name)}
                  </div>
                </div>

                {/* Expanded Charts Section */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-700">
                    {renderServiceCharts(service.name)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderJobsTab = () => {
    if (!jobs) return <div className="text-gray-400">No data available</div>;

    const videoChartData = jobs.videoExportTimeSeries?.map(d => ({ ...d, time: d.timestamp })) || [];
    const sessionChartData = jobs.sessionUploadTimeSeries?.map(d => ({ ...d, time: d.timestamp })) || [];

    return (
      <div className="space-y-6">
        {/* Session Uploads Chart */}
        {sessionChartData.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Session Uploads <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={sessionChartData}>
                <defs>
                  <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" tickFormatter={formatChartTime} stroke="#6B7280" fontSize={10} />
                <YAxis stroke="#6B7280" fontSize={10} />
                <Tooltip content={<CustomTooltip />} />
                {!isSeriesHidden("jobs:sessions", "Sessions") && (
                  <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#3B82F6" fillOpacity={1} fill="url(#colorSessions)" />
                )}
                {!isSeriesHidden("jobs:sessions", "Users") && (
                  <Line type="monotone" dataKey="users" name="Users" stroke="#10B981" dot={false} strokeWidth={2} />
                )}
              </AreaChart>
            </ResponsiveContainer>
            {renderToggleableLegend("jobs:sessions", [
              { name: "Sessions", color: "#3B82F6" },
              { name: "Users", color: "#10B981" },
            ])}
          </div>
        )}

        {/* Surfi Video Creations */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Surfi Video Creations</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div>
              <span className="text-gray-500 text-sm">Pending</span>
              <p className="text-xl font-bold text-yellow-400">{jobs.videoExports.pendingTotal ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Requested</span>
              <p className="text-xl font-bold text-blue-400">{jobs.videoExports.requested ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Created</span>
              <p className="text-xl font-bold text-green-400">{jobs.videoExports.created ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Downloaded</span>
              <p className="text-xl font-bold text-white">{jobs.videoExports.downloaded ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Errors</span>
              <p className="text-xl font-bold text-red-400">{jobs.videoExports.error ?? 0}</p>
            </div>
          </div>

          {/* Video Export Chart */}
          {videoChartData.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <h4 className="text-xs font-medium text-gray-400 mb-2">Export Timeline <span className="text-gray-500 font-normal">(click legend to toggle)</span></h4>
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={videoChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="time" tickFormatter={formatChartTime} stroke="#6B7280" fontSize={10} />
                  <YAxis stroke="#6B7280" fontSize={10} />
                  <Tooltip content={<CustomTooltip />} />
                  {!isSeriesHidden("jobs:video", "Created") && (
                    <Area type="monotone" dataKey="created" name="Created" stroke="#10B981" fill="#10B981" fillOpacity={0.2} stackId="1" />
                  )}
                  {!isSeriesHidden("jobs:video", "Errors") && (
                    <Area type="monotone" dataKey="errors" name="Errors" stroke="#EF4444" fill="#EF4444" fillOpacity={0.2} stackId="2" />
                  )}
                </AreaChart>
              </ResponsiveContainer>
              {renderToggleableLegend("jobs:video", [
                { name: "Created", color: "#10B981" },
                { name: "Errors", color: "#EF4444" },
              ])}
            </div>
          )}
        </div>

        {/* Moosend Syncs */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Moosend Sync History</h3>
          {jobs.moosendSyncs.length === 0 ? (
            <p className="text-gray-500">No sync logs available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-left">
                    <th className="pb-2">Started</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2">Added</th>
                    <th className="pb-2">Updated</th>
                    <th className="pb-2">Errors</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  {jobs.moosendSyncs.map((sync, idx) => (
                    <tr key={idx} className="border-t border-gray-700">
                      <td className="py-2">{formatTimestamp(sync.startedAt)}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          sync.status === "completed" ? "bg-green-500/20 text-green-400" :
                          sync.status === "failed" ? "bg-red-500/20 text-red-400" :
                          "bg-yellow-500/20 text-yellow-400"
                        }`}>
                          {sync.status}
                        </span>
                      </td>
                      <td className="py-2">{sync.usersAdded}</td>
                      <td className="py-2">{sync.usersUpdated}</td>
                      <td className="py-2">{sync.errors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderIntegrationsTab = () => {
    if (!integrations) return <div className="text-gray-400">No data available</div>;

    return (
      <div className="space-y-4">
        {/* Moosend */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Moosend</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(integrations.moosend.health)}`}>
              {integrations.moosend.health}
            </span>
          </div>

          {/* Sync History Table */}
          {integrations.moosend.syncHistory && integrations.moosend.syncHistory.length > 0 ? (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-400 mb-2">Recent Sync History</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-700">
                      <th className="text-left py-2 pr-3">Started</th>
                      <th className="text-left py-2 pr-3">Completed</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-right py-2 pr-3">Added</th>
                      <th className="text-right py-2 pr-3">Updated</th>
                      <th className="text-right py-2">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrations.moosend.syncHistory.map((sync, idx) => (
                      <tr
                        key={idx}
                        className={`border-b border-gray-700/50 ${
                          sync.isLatest
                            ? 'bg-blue-500/10 font-medium'
                            : 'hover:bg-gray-700/30'
                        }`}
                      >
                        <td className="py-2 pr-3 text-gray-300">
                          {sync.isLatest && <span className="text-blue-400 mr-1">●</span>}
                          {formatTimestamp(sync.syncStarted)}
                        </td>
                        <td className="py-2 pr-3 text-gray-300">{formatTimestamp(sync.syncCompleted)}</td>
                        <td className="py-2 pr-3">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            sync.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            sync.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {sync.status ?? 'unknown'}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-right text-gray-300">{sync.usersAdded}</td>
                        <td className="py-2 pr-3 text-right text-gray-300">{sync.usersUpdated}</td>
                        <td className={`py-2 text-right ${sync.errors > 0 ? 'text-red-400' : 'text-gray-300'}`}>
                          {sync.errors}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500">Last Sync</span>
                <p className="text-white">{formatTimestamp(integrations.moosend.lastSyncCompleted)}</p>
              </div>
              <div>
                <span className="text-gray-500">Status</span>
                <p className="text-white">{integrations.moosend.lastSyncStatus ?? "N/A"}</p>
              </div>
              <div>
                <span className="text-gray-500">Last Added</span>
                <p className="text-white">{integrations.moosend.lastUsersAdded ?? 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Last Errors</span>
                <p className="text-white">{integrations.moosend.lastErrors ?? 0}</p>
              </div>
            </div>
          )}

          {integrations.moosend.stateDistribution && Object.keys(integrations.moosend.stateDistribution).length > 0 && (
            <div className="pt-4 border-t border-gray-700">
              <h4 className="text-sm font-medium text-gray-400 mb-2">Sync State Distribution</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(integrations.moosend.stateDistribution).map(([state, count]) => (
                  <span key={state} className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                    {state}: {count.toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Garmin */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Garmin</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(integrations.garmin.health)}`}>
              {integrations.garmin.health}
            </span>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-4">
            <div>
              <span className="text-gray-500">Connected Users</span>
              <p className="text-white">{typeof integrations.garmin.connectedUsers === 'number' ? integrations.garmin.connectedUsers.toLocaleString() : integrations.garmin.connectedUsers ?? "N/A"}</p>
            </div>
            <div>
              <span className="text-gray-500">Successful Imports</span>
              <p className="text-green-400">{integrations.garmin.successCount?.toLocaleString() ?? "N/A"}</p>
            </div>
            <div>
              <span className="text-gray-500">Rejected (Non-Surfr)</span>
              <p className="text-yellow-400">{integrations.garmin.rejectedCount?.toLocaleString() ?? "N/A"}</p>
            </div>
            <div>
              <span className="text-gray-500">Errors</span>
              <p className="text-red-400">{integrations.garmin.errorCount ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500">Total Executions</span>
              <p className="text-white">{integrations.garmin.totalExecutions?.toLocaleString() ?? "N/A"}</p>
            </div>
          </div>

          {integrations.garmin.note && (
            <p className="text-gray-500 text-xs mb-4">{integrations.garmin.note}</p>
          )}

          {/* Garmin Import Status Time Series Chart */}
          {integrations.garmin.importTimeSeries && integrations.garmin.importTimeSeries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <h4 className="text-sm font-medium text-gray-400 mb-3">Import Status Over Time <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h4>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={integrations.garmin.importTimeSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={formatChartTime}
                      stroke="#9CA3AF"
                      fontSize={10}
                    />
                    <YAxis stroke="#9CA3AF" fontSize={10} />
                    <Tooltip content={<CustomTooltip />} />
                    {!isSeriesHidden("integrations:garmin-import", "Success") && (
                      <Area
                        type="monotone"
                        dataKey="success"
                        name="Success"
                        stroke="#10B981"
                        fill="#10B981"
                        fillOpacity={0.3}
                        stackId="1"
                      />
                    )}
                    {!isSeriesHidden("integrations:garmin-import", "Rejected") && (
                      <Area
                        type="monotone"
                        dataKey="rejected"
                        name="Rejected"
                        stroke="#F59E0B"
                        fill="#F59E0B"
                        fillOpacity={0.3}
                        stackId="1"
                      />
                    )}
                    {!isSeriesHidden("integrations:garmin-import", "Errors") && (
                      <Area
                        type="monotone"
                        dataKey="errors"
                        name="Errors"
                        stroke="#EF4444"
                        fill="#EF4444"
                        fillOpacity={0.3}
                        stackId="1"
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {renderToggleableLegend("integrations:garmin-import", [
                { name: "Success", color: "#10B981" },
                { name: "Rejected", color: "#F59E0B" },
                { name: "Errors", color: "#EF4444" },
              ])}
            </div>
          )}

          {/* Legacy Garmin Imports Chart (sessions-based) */}
          {integrations.garminTimeSeries && integrations.garminTimeSeries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <h4 className="text-sm font-medium text-gray-400 mb-3">Sessions Imported <span className="text-gray-500 font-normal text-xs">(from database)</span></h4>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={integrations.garminTimeSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={formatChartTime}
                      stroke="#9CA3AF"
                      fontSize={10}
                    />
                    <YAxis stroke="#9CA3AF" fontSize={10} />
                    <Tooltip content={<CustomTooltip />} />
                    {!isSeriesHidden("integrations:garmin", "Imports") && (
                      <Area
                        type="monotone"
                        dataKey="imports"
                        name="Imports"
                        stroke="#10B981"
                        fill="#10B981"
                        fillOpacity={0.3}
                      />
                    )}
                    {!isSeriesHidden("integrations:garmin", "Unique Users") && (
                      <Area
                        type="monotone"
                        dataKey="users"
                        name="Unique Users"
                        stroke="#3B82F6"
                        fill="#3B82F6"
                        fillOpacity={0.3}
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {renderToggleableLegend("integrations:garmin", [
                { name: "Imports", color: "#10B981" },
                { name: "Unique Users", color: "#3B82F6" },
              ])}
            </div>
          )}
        </div>

        {/* Cloud Functions */}
        {integrations.cloudFunctions && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Cloud Functions</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(integrations.cloudFunctions.health)}`}>
                {integrations.cloudFunctions.health}
              </span>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500">Total Executions</span>
                <p className="text-white">{integrations.cloudFunctions.totalExecutions?.toLocaleString() ?? 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Total Errors</span>
                <p className={integrations.cloudFunctions.totalErrors > 0 ? "text-red-400" : "text-white"}>
                  {integrations.cloudFunctions.totalErrors ?? 0}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Functions</span>
                <p className="text-white">{integrations.cloudFunctions.functions?.length ?? 0}</p>
              </div>
            </div>

            {integrations.cloudFunctions.note && (
              <p className="text-gray-500 text-xs mb-4">{integrations.cloudFunctions.note}</p>
            )}

            {/* Functions Table */}
            {integrations.cloudFunctions.functions && integrations.cloudFunctions.functions.length > 0 && (
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-700">
                      <th className="text-left py-2 pr-3">Function</th>
                      <th className="text-right py-2 pr-3">Executions</th>
                      <th className="text-right py-2 pr-3">Success</th>
                      <th className="text-right py-2 pr-3">Errors</th>
                      <th className="text-right py-2 pr-3">Avg Duration</th>
                      <th className="text-right py-2">Generation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrations.cloudFunctions.functions.map((func, idx) => (
                      <tr key={idx} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-2 pr-3 text-gray-300 font-mono text-xs">{func.name}</td>
                        <td className="py-2 pr-3 text-right text-gray-300">{func.executions.toLocaleString()}</td>
                        <td className="py-2 pr-3 text-right text-green-400">{func.success.toLocaleString()}</td>
                        <td className={`py-2 pr-3 text-right ${func.errors > 0 ? 'text-red-400' : 'text-gray-300'}`}>
                          {func.errors}
                        </td>
                        <td className="py-2 pr-3 text-right text-gray-300">
                          {func.avgDurationMs != null ? `${func.avgDurationMs}ms` : '-'}
                        </td>
                        <td className="py-2 text-right text-gray-500">{func.generation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Time Series Chart - Per Function */}
            {integrations.cloudFunctions.timeSeries && integrations.cloudFunctions.timeSeries.length > 0 && (() => {
              // Extract function names from time series data (excluding timestamp, executions, errors)
              const functionKeys = Object.keys(integrations.cloudFunctions.timeSeries[0] || {})
                .filter(k => !['timestamp', 'executions', 'errors'].includes(k));
              const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#EF4444', '#06B6D4', '#84CC16'];
              const legendItems = functionKeys.map((key, i) => ({ name: key, color: colors[i % colors.length] }));

              return (
                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Executions Over Time <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={integrations.cloudFunctions.timeSeries}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#9CA3AF" fontSize={10} />
                        <YAxis stroke="#9CA3AF" fontSize={10} />
                        <Tooltip content={<CustomTooltip />} />
                        {functionKeys.map((funcKey, i) => (
                          !isSeriesHidden("integrations:cloudFunctions", funcKey) && (
                            <Area
                              key={funcKey}
                              type="monotone"
                              dataKey={funcKey}
                              name={funcKey}
                              stroke={colors[i % colors.length]}
                              fill={colors[i % colors.length]}
                              fillOpacity={0.3}
                              stackId="1"
                            />
                          )
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  {renderToggleableLegend("integrations:cloudFunctions", legendItems)}
                </div>
              );
            })()}
          </div>
        )}

        {/* Cloud Tasks */}
        {integrations.cloudTasks && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Cloud Tasks</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(integrations.cloudTasks.health)}`}>
                {integrations.cloudTasks.health}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500">Total Tasks</span>
                <p className="text-white">{integrations.cloudTasks.totalTasks?.toLocaleString() ?? 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Queues</span>
                <p className="text-white">{integrations.cloudTasks.queues?.length ?? 0}</p>
              </div>
            </div>

            {integrations.cloudTasks.note && (
              <p className="text-gray-500 text-xs mb-4">{integrations.cloudTasks.note}</p>
            )}

            {/* Queues */}
            {integrations.cloudTasks.queues && integrations.cloudTasks.queues.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {integrations.cloudTasks.queues.map((queue, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                    {queue.name}: {queue.taskCount.toLocaleString()}
                  </span>
                ))}
              </div>
            )}

            {/* Time Series Chart - Per Queue */}
            {integrations.cloudTasks.timeSeries && integrations.cloudTasks.timeSeries.length > 0 && (() => {
              // Extract queue names from time series data (excluding timestamp, tasks)
              const queueKeys = Object.keys(integrations.cloudTasks.timeSeries[0] || {})
                .filter(k => !['timestamp', 'tasks'].includes(k));
              const colors = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#EF4444'];
              const legendItems = queueKeys.map((key, i) => ({ name: key, color: colors[i % colors.length] }));

              return (
                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Tasks Over Time <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={integrations.cloudTasks.timeSeries}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#9CA3AF" fontSize={10} />
                        <YAxis stroke="#9CA3AF" fontSize={10} />
                        <Tooltip content={<CustomTooltip />} />
                        {queueKeys.length > 0 ? (
                          queueKeys.map((queueKey, i) => (
                            !isSeriesHidden("integrations:cloudTasks", queueKey) && (
                              <Area
                                key={queueKey}
                                type="monotone"
                                dataKey={queueKey}
                                name={queueKey}
                                stroke={colors[i % colors.length]}
                                fill={colors[i % colors.length]}
                                fillOpacity={0.3}
                                stackId="1"
                              />
                            )
                          ))
                        ) : (
                          <Area type="monotone" dataKey="tasks" name="Tasks" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  {queueKeys.length > 0 && renderToggleableLegend("integrations:cloudTasks", legendItems)}
                </div>
              );
            })()}
          </div>
        )}

        {/* Cron Jobs */}
        {integrations.cronJobs && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Cron Jobs</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(integrations.cronJobs.health)}`}>
                {integrations.cronJobs.health}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500">Total Executions</span>
                <p className="text-white">{integrations.cronJobs.totalExecutions?.toLocaleString() ?? 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Errors</span>
                <p className={integrations.cronJobs.totalErrors > 0 ? "text-red-400" : "text-white"}>
                  {integrations.cronJobs.totalErrors ?? 0}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Jobs</span>
                <p className="text-white">{integrations.cronJobs.jobs?.length ?? 0}</p>
              </div>
            </div>

            {integrations.cronJobs.note && (
              <p className="text-gray-500 text-xs mb-4">{integrations.cronJobs.note}</p>
            )}

            {/* Jobs Table */}
            {integrations.cronJobs.jobs && integrations.cronJobs.jobs.length > 0 && (
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-700">
                      <th className="text-left py-2 pr-3">Endpoint</th>
                      <th className="text-right py-2 pr-3">Executions</th>
                      <th className="text-right py-2 pr-3">Success</th>
                      <th className="text-right py-2 pr-3">Errors</th>
                      <th className="text-right py-2 pr-3">Avg Duration</th>
                      <th className="text-right py-2">Last Run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrations.cronJobs.jobs.map((job, idx) => (
                      <tr key={idx} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-2 pr-3 text-gray-300 font-mono text-xs">{job.endpoint}</td>
                        <td className="py-2 pr-3 text-right text-gray-300">{job.executions.toLocaleString()}</td>
                        <td className="py-2 pr-3 text-right text-green-400">{job.success.toLocaleString()}</td>
                        <td className={`py-2 pr-3 text-right ${job.errors > 0 ? 'text-red-400' : 'text-gray-300'}`}>
                          {job.errors}
                        </td>
                        <td className="py-2 pr-3 text-right text-gray-300">
                          {job.avgDurationMs != null ? `${job.avgDurationMs}ms` : '-'}
                        </td>
                        <td className="py-2 text-right text-gray-500 text-xs">{formatTimestamp(job.lastRun)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Time Series Chart */}
            {integrations.cronJobs.timeSeries && integrations.cronJobs.timeSeries.length > 0 && (
              <div className="pt-4 border-t border-gray-700">
                <h4 className="text-sm font-medium text-gray-400 mb-3">Executions Over Time</h4>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={integrations.cronJobs.timeSeries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#9CA3AF" fontSize={10} />
                      <YAxis stroke="#9CA3AF" fontSize={10} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="executions" name="Executions" stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="errors" name="Errors" stroke="#EF4444" fill="#EF4444" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pub/Sub */}
        {integrations.pubsub && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Pub/Sub Topics</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(integrations.pubsub.health)}`}>
                {integrations.pubsub.health}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500">Total Messages</span>
                <p className="text-white">{integrations.pubsub.totalMessages?.toLocaleString() ?? 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Topics</span>
                <p className="text-white">{integrations.pubsub.topics?.length ?? 0}</p>
              </div>
            </div>

            {integrations.pubsub.note && (
              <p className="text-gray-500 text-xs mb-4">{integrations.pubsub.note}</p>
            )}

            {/* Topics */}
            {integrations.pubsub.topics && integrations.pubsub.topics.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {integrations.pubsub.topics.map((topic, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                    {topic.name}: {topic.messageCount.toLocaleString()}
                  </span>
                ))}
              </div>
            )}

            {/* Time Series Chart - Per Topic */}
            {integrations.pubsub.timeSeries && integrations.pubsub.timeSeries.length > 0 && (
              <div className="pt-4 border-t border-gray-700">
                <h4 className="text-sm font-medium text-gray-400 mb-3">Messages Over Time <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={integrations.pubsub.timeSeries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#9CA3AF" fontSize={10} />
                      <YAxis stroke="#9CA3AF" fontSize={10} />
                      <Tooltip content={<CustomTooltip />} />
                      {!isSeriesHidden("integrations:pubsub", "garminSessionImports") && (
                        <Area type="monotone" dataKey="garminSessionImports" name="garmin-session-imports" stroke="#10B981" fill="#10B981" fillOpacity={0.3} stackId="1" />
                      )}
                      {!isSeriesHidden("integrations:pubsub", "jumpVideoExports") && (
                        <Area type="monotone" dataKey="jumpVideoExports" name="jump-video-exports" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} stackId="1" />
                      )}
                      {!isSeriesHidden("integrations:pubsub", "surfrSessionSaveEvents") && (
                        <Area type="monotone" dataKey="surfrSessionSaveEvents" name="surfr-session-save-events" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} stackId="1" />
                      )}
                      {!isSeriesHidden("integrations:pubsub", "surfrSessionLiveEvents") && (
                        <Area type="monotone" dataKey="surfrSessionLiveEvents" name="surfr-session-live-events" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} stackId="1" />
                      )}
                      {!isSeriesHidden("integrations:pubsub", "surfrSessionEventsPartnerTwg") && (
                        <Area type="monotone" dataKey="surfrSessionEventsPartnerTwg" name="surfr-session-events-partner-twg" stroke="#EC4899" fill="#EC4899" fillOpacity={0.3} stackId="1" />
                      )}
                      {!isSeriesHidden("integrations:pubsub", "systemAlertNotifications") && (
                        <Area type="monotone" dataKey="systemAlertNotifications" name="system-alert-notifications" stroke="#EF4444" fill="#EF4444" fillOpacity={0.3} stackId="1" />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                {renderToggleableLegend("integrations:pubsub", [
                  { name: "garminSessionImports", color: "#10B981" },
                  { name: "jumpVideoExports", color: "#3B82F6" },
                  { name: "surfrSessionSaveEvents", color: "#8B5CF6" },
                  { name: "surfrSessionLiveEvents", color: "#F59E0B" },
                  { name: "surfrSessionEventsPartnerTwg", color: "#EC4899" },
                  { name: "systemAlertNotifications", color: "#EF4444" },
                ])}
              </div>
            )}
          </div>
        )}

        {/* Receipts */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Receipt Validation</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(integrations.receipts.health)}`}>
              {integrations.receipts.health}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total</span>
              <p className="text-white">{integrations.receipts.total?.toLocaleString() ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500">Active</span>
              <p className="text-green-400">{integrations.receipts.active?.toLocaleString() ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500">iOS</span>
              <p className="text-white">{integrations.receipts.ios?.toLocaleString() ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500">Android</span>
              <p className="text-white">{integrations.receipts.android?.toLocaleString() ?? 0}</p>
            </div>
            <div>
              <span className="text-gray-500">Errors</span>
              <p className="text-red-400">{integrations.receipts.errors ?? 0}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDatabaseTab = () => {
    if (!database) return <div className="text-gray-400">No data available</div>;

    // Sort materialized views
    const sortedMvs = [...database.materializedViews].sort((a, b) => {
      const direction = mvSort.direction === "asc" ? 1 : -1;
      switch (mvSort.key) {
        case "name":
          return direction * a.name.localeCompare(b.name);
        case "rowCount":
          return direction * ((a.rowCount ?? 0) - (b.rowCount ?? 0));
        case "size":
          return direction * ((a.sizeBytes ?? parseSizeToBytes(a.size)) - (b.sizeBytes ?? parseSizeToBytes(b.size)));
        case "lastRefresh":
          const aTime = a.lastRefresh ? new Date(a.lastRefresh).getTime() : 0;
          const bTime = b.lastRefresh ? new Date(b.lastRefresh).getTime() : 0;
          return direction * (aTime - bTime);
        default:
          return 0;
      }
    });

    // Sort tables
    const sortedTables = [...database.tables].sort((a, b) => {
      const direction = tableSort.direction === "asc" ? 1 : -1;
      switch (tableSort.key) {
        case "name":
          return direction * a.name.localeCompare(b.name);
        case "rowCount":
          return direction * ((a.rowCount ?? 0) - (b.rowCount ?? 0));
        case "size":
          return direction * ((a.sizeBytes ?? parseSizeToBytes(a.size)) - (b.sizeBytes ?? parseSizeToBytes(b.size)));
        default:
          return 0;
      }
    });

    return (
      <div className="space-y-6">
        {/* Materialized Views - First */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Materialized Views</h3>
            <span className="text-xs text-gray-500">{database.materializedViews.length} views</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-left">
                  <th
                    className="pb-2 cursor-pointer hover:text-gray-200 select-none"
                    onClick={() => handleSort(mvSort, setMvSort, "name")}
                  >
                    View <SortIcon sortKey="name" currentSort={mvSort} />
                  </th>
                  <th
                    className="pb-2 text-right cursor-pointer hover:text-gray-200 select-none"
                    onClick={() => handleSort(mvSort, setMvSort, "rowCount")}
                  >
                    Rows <SortIcon sortKey="rowCount" currentSort={mvSort} />
                  </th>
                  <th
                    className="pb-2 text-right cursor-pointer hover:text-gray-200 select-none"
                    onClick={() => handleSort(mvSort, setMvSort, "size")}
                  >
                    Size <SortIcon sortKey="size" currentSort={mvSort} />
                  </th>
                  <th
                    className="pb-2 text-right cursor-pointer hover:text-gray-200 select-none"
                    onClick={() => handleSort(mvSort, setMvSort, "lastRefresh")}
                  >
                    Last Refresh <SortIcon sortKey="lastRefresh" currentSort={mvSort} />
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                {sortedMvs.map((view) => (
                  <tr key={view.name} className="border-t border-gray-700 hover:bg-gray-700/50">
                    <td className="py-2 font-mono text-xs">{view.name}</td>
                    <td className="py-2 text-right">{view.rowCount?.toLocaleString() ?? "-"}</td>
                    <td className="py-2 text-right">{view.size}</td>
                    <td className="py-2 text-right text-xs" title={view.lastRefresh ? formatTimestamp(view.lastRefresh) : undefined}>
                      {view.lastRefresh ? (
                        <span className="text-green-400">{formatRelativeTime(view.lastRefresh)}</span>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Table Statistics */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Table Statistics</h3>
            <span className="text-xs text-gray-500">{database.tables.length} tables</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-left">
                  <th
                    className="pb-2 cursor-pointer hover:text-gray-200 select-none"
                    onClick={() => handleSort(tableSort, setTableSort, "name")}
                  >
                    Table <SortIcon sortKey="name" currentSort={tableSort} />
                  </th>
                  <th
                    className="pb-2 text-right cursor-pointer hover:text-gray-200 select-none"
                    onClick={() => handleSort(tableSort, setTableSort, "rowCount")}
                  >
                    Rows <SortIcon sortKey="rowCount" currentSort={tableSort} />
                  </th>
                  <th
                    className="pb-2 text-right cursor-pointer hover:text-gray-200 select-none"
                    onClick={() => handleSort(tableSort, setTableSort, "size")}
                  >
                    Size <SortIcon sortKey="size" currentSort={tableSort} />
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                {sortedTables.map((table) => (
                  <tr key={table.name} className="border-t border-gray-700 hover:bg-gray-700/50">
                    <td className="py-2 font-mono text-xs">{table.name}</td>
                    <td className="py-2 text-right">{table.rowCount?.toLocaleString()}</td>
                    <td className="py-2 text-right">{table.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Connection Pool - Simplified as a compact info bar */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-sm font-medium text-gray-400">Connection Pool</h3>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-300">
                  <span className="text-blue-400 font-semibold">{database.connectionPool.activeConnections ?? 0}</span>
                  <span className="text-gray-500"> active</span>
                </span>
                <span className="text-gray-300">
                  <span className="text-green-400 font-semibold">{database.connectionPool.idleConnections ?? 0}</span>
                  <span className="text-gray-500"> idle</span>
                </span>
                <span className="text-gray-300">
                  <span className="text-white font-semibold">{database.connectionPool.totalConnections ?? 0}</span>
                  <span className="text-gray-500"> / {database.connectionPool.maximumPoolSize ?? 20}</span>
                </span>
                {(database.connectionPool.threadsAwaitingConnection ?? 0) > 0 && (
                  <span className="text-yellow-400">
                    {database.connectionPool.threadsAwaitingConnection} waiting
                  </span>
                )}
              </div>
            </div>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              database.connectionPool.health === "HEALTHY" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
            }`}>
              {database.connectionPool.health}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderUsersTab = () => {
    if (!users) return <div className="text-gray-400">No data available</div>;

    return (
      <div className="space-y-6">
        {/* User Counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Total Users</div>
            <div className="text-2xl font-bold text-white mt-1">{users.counts.total?.toLocaleString()}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">New ({hours}h)</div>
            <div className="text-2xl font-bold text-blue-400 mt-1">{users.counts.newInPeriod?.toLocaleString()}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Pro Users</div>
            <div className="text-2xl font-bold text-purple-400 mt-1">{users.counts.pro?.toLocaleString()}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Free Sub</div>
            <div className="text-2xl font-bold text-green-400 mt-1">{users.counts.freeSubscription?.toLocaleString()}</div>
          </div>
        </div>

        {/* Subscriptions */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Active Subscriptions</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="text-gray-500 text-sm">iOS Active</span>
              <p className="text-xl font-bold text-white">{users.subscriptions.iosActive?.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Android Active</span>
              <p className="text-xl font-bold text-white">{users.subscriptions.androidActive?.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Validation Errors</span>
              <p className="text-xl font-bold text-red-400">{users.subscriptions.errors}</p>
            </div>
          </div>
        </div>

        {/* Activity */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Activity ({hours}h)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="text-gray-500 text-sm">Sessions Uploaded</span>
              <p className="text-xl font-bold text-white">{users.activity.sessionsUploaded?.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Jumps Recorded</span>
              <p className="text-xl font-bold text-white">{users.activity.jumpsRecorded?.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Active Livestreams</span>
              <p className="text-xl font-bold text-green-400">{users.activity.activeLivestreams}</p>
            </div>
          </div>
        </div>

        {/* User Activity Time Series Chart */}
        {users.timeSeries && users.timeSeries.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">User Activity Over Time <span className="text-gray-500 font-normal text-sm">(click legend to toggle)</span></h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={users.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatChartTime}
                    stroke="#9CA3AF"
                    fontSize={10}
                  />
                  <YAxis stroke="#9CA3AF" fontSize={10} />
                  <Tooltip content={<CustomTooltip />} />
                  {!isSeriesHidden("users:activity", "New Users") && (
                    <Area
                      type="monotone"
                      dataKey="newUsers"
                      name="New Users"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.3}
                    />
                  )}
                  {!isSeriesHidden("users:activity", "Active Users") && (
                    <Area
                      type="monotone"
                      dataKey="activeUsers"
                      name="Active Users"
                      stroke="#10B981"
                      fill="#10B981"
                      fillOpacity={0.3}
                    />
                  )}
                  {!isSeriesHidden("users:activity", "Sessions Uploaded") && (
                    <Area
                      type="monotone"
                      dataKey="sessions"
                      name="Sessions Uploaded"
                      stroke="#F59E0B"
                      fill="#F59E0B"
                      fillOpacity={0.3}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {renderToggleableLegend("users:activity", [
              { name: "New Users", color: "#3B82F6" },
              { name: "Active Users", color: "#10B981" },
              { name: "Sessions Uploaded", color: "#F59E0B" },
            ])}
          </div>
        )}
      </div>
    );
  };

  const renderErrorsTab = () => {
    if (!errors) return <div className="text-gray-400">No data available</div>;

    const totalErrors =
      (errors.receiptErrors?.length ?? 0) +
      (errors.moosendErrors?.length ?? 0) +
      (errors.videoExportErrors?.length ?? 0);

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Receipt Errors</div>
            <div className="text-2xl font-bold text-red-400 mt-1">{errors.receiptErrors?.length ?? 0}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Moosend Errors</div>
            <div className="text-2xl font-bold text-red-400 mt-1">{errors.moosendErrors?.length ?? 0}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Surfi Errors</div>
            <div className="text-2xl font-bold text-red-400 mt-1">{errors.videoExportErrors?.length ?? 0}</div>
          </div>
        </div>

        {/* API Error Time Series Chart */}
        {errors.timeSeries && errors.timeSeries.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">API Errors Over Time <span className="text-gray-500 font-normal text-sm">(click legend to toggle)</span></h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={errors.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatChartTime}
                    stroke="#9CA3AF"
                    fontSize={10}
                  />
                  <YAxis stroke="#9CA3AF" fontSize={10} />
                  <Tooltip content={<CustomTooltip />} />
                  {!isSeriesHidden("errors:api", "5xx Server Errors") && (
                    <Area
                      type="monotone"
                      dataKey="serverErrors"
                      name="5xx Server Errors"
                      stroke="#EF4444"
                      fill="#EF4444"
                      fillOpacity={0.3}
                      stackId="errors"
                    />
                  )}
                  {!isSeriesHidden("errors:api", "4xx Client Errors") && (
                    <Area
                      type="monotone"
                      dataKey="clientErrors"
                      name="4xx Client Errors"
                      stroke="#F59E0B"
                      fill="#F59E0B"
                      fillOpacity={0.3}
                      stackId="errors"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {renderToggleableLegend("errors:api", [
              { name: "5xx Server Errors", color: "#EF4444" },
              { name: "4xx Client Errors", color: "#F59E0B" },
            ])}
            {/* HTTP Status Code Breakdown */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <h4 className="text-sm font-medium text-gray-400 mb-3">Server Error Breakdown <span className="text-gray-500 font-normal text-xs">(click legend to toggle)</span></h4>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={errors.timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={formatChartTime}
                      stroke="#9CA3AF"
                      fontSize={10}
                    />
                    <YAxis stroke="#9CA3AF" fontSize={10} />
                    <Tooltip content={<CustomTooltip />} />
                    {!isSeriesHidden("errors:breakdown", "500 Internal") && (
                      <Line
                        type="monotone"
                        dataKey="http500"
                        name="500 Internal"
                        stroke="#EF4444"
                        dot={false}
                      />
                    )}
                    {!isSeriesHidden("errors:breakdown", "502 Bad Gateway") && (
                      <Line
                        type="monotone"
                        dataKey="http502"
                        name="502 Bad Gateway"
                        stroke="#F97316"
                        dot={false}
                      />
                    )}
                    {!isSeriesHidden("errors:breakdown", "503 Unavailable") && (
                      <Line
                        type="monotone"
                        dataKey="http503"
                        name="503 Unavailable"
                        stroke="#EAB308"
                        dot={false}
                      />
                    )}
                    {!isSeriesHidden("errors:breakdown", "504 Timeout") && (
                      <Line
                        type="monotone"
                        dataKey="http504"
                        name="504 Timeout"
                        stroke="#A855F7"
                        dot={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {renderToggleableLegend("errors:breakdown", [
                { name: "500 Internal", color: "#EF4444" },
                { name: "502 Bad Gateway", color: "#F97316" },
                { name: "503 Unavailable", color: "#EAB308" },
                { name: "504 Timeout", color: "#A855F7" },
              ])}
            </div>
          </div>
        )}

        {/* Receipt Errors */}
        {errors.receiptErrors && errors.receiptErrors.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Receipt Validation Errors</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-left">
                    <th className="pb-2">User ID</th>
                    <th className="pb-2">Platform</th>
                    <th className="pb-2">Error</th>
                    <th className="pb-2">Last Check</th>
                    <th className="pb-2">Count</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  {errors.receiptErrors.slice(0, 10).map((error, idx) => (
                    <tr key={idx} className="border-t border-gray-700">
                      <td className="py-2">{error.userId}</td>
                      <td className="py-2">{error.platform}</td>
                      <td className="py-2 text-red-400 text-xs max-w-xs truncate">{error.error}</td>
                      <td className="py-2 text-xs">{formatTimestamp(error.lastCheck)}</td>
                      <td className="py-2">{error.errorCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Surfi Video Creation Errors */}
        {errors.videoExportErrors && errors.videoExportErrors.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Surfi Video Creation Errors</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-left">
                    <th className="pb-2">ID</th>
                    <th className="pb-2">User ID</th>
                    <th className="pb-2">Created</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  {errors.videoExportErrors.slice(0, 10).map((error) => (
                    <tr key={error.id} className="border-t border-gray-700">
                      <td className="py-2">{error.id}</td>
                      <td className="py-2">{error.userId}</td>
                      <td className="py-2 text-xs">{formatTimestamp(error.created)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* API Errors with Trace Logs */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">API Server Errors (5xx)</h3>
            <button
              onClick={loadApiErrors}
              disabled={loadingApiErrors}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 rounded text-sm text-white transition-colors"
            >
              {loadingApiErrors ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Loading...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-4 h-4" />
                  Load Error Logs
                </>
              )}
            </button>
          </div>

          {!apiErrors && !loadingApiErrors && (
            <p className="text-gray-500 text-sm">
              Click "Load Error Logs" to fetch recent API errors with detailed stack traces from Cloud Logging.
            </p>
          )}

          {apiErrors && apiErrors.errors.length === 0 && (
            <div className="text-center py-4">
              <CheckCircleIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-green-300 text-sm">No API errors in the selected time period</p>
            </div>
          )}

          {apiErrors && apiErrors.errors.length > 0 && (
            <div className="space-y-2">
              <p className="text-gray-400 text-xs mb-3">
                Showing {apiErrors.errors.length} errors. Click on an error to view detailed logs.
              </p>
              {apiErrors.errors.map((error, idx) => {
                const isExpanded = error.traceId && expandedTraces.has(error.traceId);
                return (
                  <div key={idx} className="border border-gray-700 rounded-lg overflow-hidden">
                    <div
                      onClick={() => error.traceId && toggleTraceLogs(error.traceId)}
                      className={`p-3 cursor-pointer hover:bg-gray-750 transition-colors ${
                        error.traceId ? '' : 'cursor-default'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {error.traceId ? (
                          isExpanded ? (
                            <ChevronDownIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          )
                        ) : (
                          <div className="w-4" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              error.status === 500 ? 'bg-red-500/30 text-red-300' :
                              error.status === 502 ? 'bg-orange-500/30 text-orange-300' :
                              error.status === 503 ? 'bg-yellow-500/30 text-yellow-300' :
                              error.status === 504 ? 'bg-purple-500/30 text-purple-300' :
                              'bg-red-500/30 text-red-300'
                            }`}>
                              {error.status}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              error.method === 'GET' ? 'bg-green-500/20 text-green-400' :
                              error.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                              error.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                              error.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {error.method}
                            </span>
                            <span className="text-white text-sm font-mono truncate">
                              {error.endpoint}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                            <span>{formatTimestamp(error.timestamp)}</span>
                            <span>{error.service}</span>
                            {error.latencyMs && <span>{error.latencyMs.toFixed(0)}ms</span>}
                          </div>
                        </div>
                        {loadingTraceLogs.has(error.traceId || '') && (
                          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                        )}
                      </div>
                    </div>
                    {/* Trace Logs */}
                    {isExpanded && error.traceId && renderTraceLogs(error.traceId)}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Note */}
        {errors.apiErrorsNote && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-300 text-sm">{errors.apiErrorsNote}</p>
          </div>
        )}

        {totalErrors === 0 && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
            <CheckCircleIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-green-300">No errors in the selected time period</p>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-400">Loading...</span>
        </div>
      );
    }

    switch (activeTab) {
      case "overview":
        return renderOverviewTab();
      case "services":
        return renderServicesTab();
      case "jobs":
        return renderJobsTab();
      case "integrations":
        return renderIntegrationsTab();
      case "database":
        return renderDatabaseTab();
      case "users":
        return renderUsersTab();
      case "errors":
        return renderErrorsTab();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">System Status</h1>
          <p className="text-gray-400 text-sm mt-1">
            Real-time system health and monitoring dashboard
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Time Range */}
          <div className="flex items-center gap-2">
            <select
              value={showCustomRange ? "custom" : timeRange.hours}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  setShowCustomRange(true);
                  // Set default custom range to last 24 hours
                  const now = new Date();
                  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                  setCustomEndDate(now.toISOString().slice(0, 16));
                  setCustomStartDate(yesterday.toISOString().slice(0, 16));
                } else {
                  setShowCustomRange(false);
                  setTimeRange(createRelativeTimeRange(Number(e.target.value)));
                }
              }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {TIME_RANGES.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
              <option value="custom">Custom...</option>
            </select>

            {/* Custom Date Range Inputs */}
            {showCustomRange && (
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500 text-sm">to</span>
                <input
                  type="datetime-local"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => {
                    if (customStartDate && customEndDate) {
                      const startISO = new Date(customStartDate).toISOString();
                      const endISO = new Date(customEndDate).toISOString();
                      setTimeRange(createAbsoluteTimeRange(startISO, endISO));
                    }
                  }}
                  className="px-2 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white text-xs"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* Auto Refresh Toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 border rounded-lg text-sm ${
              autoRefresh
                ? "bg-green-500/20 border-green-500/30 text-green-400"
                : "bg-gray-800 border-gray-700 text-gray-400"
            }`}
          >
            Auto
          </button>

          {/* Refresh Button */}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="pb-8">{renderTabContent()}</div>
    </div>
  );
};

export default SystemStatusPage;
